// ────────────────────────────────────────────────────────────────────
// Acrostic Notes — localized letter → title banks.
//
// Each entry starts with the required Latin letter (A–X) and reads as
// a mundane real-life note. Non-Latin-script languages fall back to
// English because the acrostic itself is spelt in Latin (SEVENHEARTS,
// QUEENDIAMONDS etc.) — a title in native script can't carry a Latin
// first letter without breaking the disguise.
//
// Coverage:
//   Localized: en, nl, es, fr, de, it, pt, pl, sv, no, da, fi, cs,
//              ro, hu, tr, id
//   Fallback → en: ru, uk, el, he, ar, hi, ja, ko, zh, zh-TW
//
// Style rules (per language):
//   • First-person, admin-y, no theatre. Same tone as iOS Notes.
//   • ASCII first letter (no accents on the leading char).
//   • Title < 40 chars ideally so it doesn't wrap in the list row.
//   • Preview is optional; when empty the original preview stays.
// ────────────────────────────────────────────────────────────────────

// ─── ENGLISH ────────────────────────────────────────────────────────
const en = {
  A: [
    { title: "Airport pickup — Sat 6pm",     preview: "Terminal 2 arrivals, gate TBC" },
    { title: "Amazon returns box",           preview: "Sneakers, phone case, coffee filter" },
    { title: "Anniversary ideas",            preview: "Restaurant, weekend break, small gift" },
    { title: "Apartment showing Thursday",   preview: "Proof of income + reference" },
    { title: "Apple ID password hint",       preview: "First pet + birth year" },
  ],
  B: [
    { title: "Book club — Thursday 8pm",     preview: "Chapters 4-9, Maya's place" },
    { title: "Birthday shopping",            preview: "Card, wine, small plant" },
    { title: "Bank appointment",             preview: "Passport + last 3 payslips" },
    { title: "BBQ Saturday",                 preview: "Buns, salad, extra ice" },
    { title: "Barber Tuesday 5:30",          preview: "Same as last time" },
  ],
  C: [
    { title: "Coffee with Mia",              preview: "Wednesday, near her office" },
    { title: "Car service due",              preview: "Booking ref in email" },
    { title: "Costco run",                   preview: "Paper towels, olive oil, pods" },
    { title: "Cake for Sunday",              preview: "Chocolate, no icing" },
    { title: "Chargers pack list",           preview: "Phone, laptop, watch" },
  ],
  D: [
    { title: "Dentist Wednesday",            preview: "9:20am — cleaning + X-ray" },
    { title: "Dry cleaning Friday",          preview: "Blue suit + two shirts" },
    { title: "Dinner reservation",           preview: "Table for 4, Sat 8pm" },
    { title: "Dog walker note",              preview: "Keys under mat" },
    { title: "Doctor follow-up",             preview: "Ask about new prescription" },
  ],
  E: [
    { title: "Email Sam back",               preview: "Re: last month's invoice" },
    { title: "Errands today",                preview: "Post office, pharmacy, cleaner" },
    { title: "Eye exam booked",              preview: "Friday 4pm — bring old glasses" },
    { title: "Electricity bill",             preview: "Autopay set up" },
    { title: "Emma's recital",               preview: "Saturday 7pm — navy dress" },
  ],
  F: [
    { title: "Flight home Sunday",           preview: "KL 1471 — window seat" },
    { title: "Farmers market Saturday",      preview: "Sourdough, tomatoes, honey" },
    { title: "Freelance invoice #204",       preview: "End of month, 30 days" },
    { title: "Furniture delivery",           preview: "10am-2pm, needs signature" },
    { title: "Friday movie night",           preview: "The new one Sam mentioned" },
  ],
  G: [
    { title: "Groceries",                    preview: "Milk, eggs, bananas, spinach" },
    { title: "Gym Tuesday",                  preview: "Legs + core, 45 min" },
    { title: "Gift for Alex",                preview: "Book + gift card" },
    { title: "Garden spring planting",       preview: "Basil, mint, tomatoes" },
    { title: "Guitar strings",               preview: "Elixir 10s — same brand" },
  ],
  H: [
    { title: "Haircut Friday 6pm",           preview: "Same length, layers shorter" },
    { title: "Home insurance renewal",       preview: "Compare 3 quotes" },
    { title: "Holiday plans — August",       preview: "Two weeks, coast" },
    { title: "Hiking Sunday",                preview: "Water, layers, sun cream" },
    { title: "Hardware store list",          preview: "Screws, sandpaper, glue" },
  ],
  I: [
    { title: "IKEA trip",                    preview: "Shelf, pots, meatballs" },
    { title: "Insurance renewal",            preview: "Quote expires in 5 days" },
    { title: "Invoice #204 follow up",       preview: "Chase Friday if unpaid" },
    { title: "Interview prep",               preview: "Company research, 3 questions" },
    { title: "Ideas for the pitch",          preview: "Story arc, one big number" },
  ],
  J: [
    { title: "Jury duty date",               preview: "Downtown courthouse, 8:30am" },
    { title: "Jazz brunch Sunday",           preview: "Reservation for 3, 12:15" },
    { title: "James — wedding gift",         preview: "Registry link + card" },
    { title: "Job application draft",        preview: "Tailor CV bullets" },
    { title: "Juice cleanse — day 2",        preview: "Green, red, ginger" },
  ],
  K: [
    { title: "Kids school pickup",           preview: "Wed early — 2:15 dismissal" },
    { title: "Kitchen renovation quotes",    preview: "Three vendors next week" },
    { title: "Karaoke Friday",               preview: "Sam's birthday, book room" },
    { title: "Kevin's new number",           preview: "Update contact" },
    { title: "Knife sharpener",              preview: "The one with two guides" },
  ],
  L: [
    { title: "Laundry Sunday",               preview: "Sheets + towels + gym" },
    { title: "Lunch with mom",               preview: "Wed 12:30, place on Main" },
    { title: "Library book due",             preview: "Tuesday — renew online" },
    { title: "London trip notes",            preview: "Hotel, transfer, day-by-day" },
    { title: "License renewal",              preview: "Passport + address proof" },
  ],
  M: [
    { title: "Mom's birthday",               preview: "May 14 — flowers + call" },
    { title: "Meeting notes Q3",             preview: "Numbers up 12%" },
    { title: "Movie night ideas",            preview: "The heist one, the doc" },
    { title: "Meal prep Sunday",             preview: "Rice, chicken, veg" },
    { title: "Marathon training week 6",     preview: "Long run Sat, tempo Tue" },
  ],
  N: [
    { title: "New passport application",     preview: "Photo, form, appointment" },
    { title: "Netflix picks",                preview: "The Icelandic one" },
    { title: "Neighbours BBQ Saturday",      preview: "Drinks + chairs" },
    { title: "Nail appointment",             preview: "Saturday 11" },
    { title: "Notebook — new one",           preview: "Dotted, A5, soft cover" },
  ],
  O: [
    { title: "Office move key dates",        preview: "Movers Tue, network Thu" },
    { title: "Optician follow-up",           preview: "New prescription" },
    { title: "Oil change due",               preview: "Service light on" },
    { title: "Online course week 2",         preview: "Two videos + quiz" },
    { title: "Orchestra tickets Friday",     preview: "Row H, seats 12 & 13" },
  ],
  P: [
    { title: "Packing list — trip",          preview: "Passport, charger, meds" },
    { title: "Pay rent",                     preview: "Due 1st, standing order" },
    { title: "Pilates class",                preview: "Saturday 9am, book Thu" },
    { title: "Passport photo",               preview: "Booth on Main St" },
    { title: "Pharmacy pickup",              preview: "Vitamin D, thyroid script" },
  ],
  Q: [
    { title: "Quarterly report draft",       preview: "Numbers Wed, deck Fri" },
    { title: "Quiz night Thursday",          preview: "Team of 4 — need one more" },
    { title: "Quiet weekend plan",           preview: "Books, walk, no phone" },
    { title: "Quotes from Jen",              preview: "Fold into intro" },
    { title: "Q1 goals check-in",            preview: "Two on track, one slipping" },
  ],
  R: [
    { title: "Rent due 1st",                 preview: "Double check standing order" },
    { title: "Recipe: mushroom risotto",     preview: "Stock, parmesan, wine" },
    { title: "Return the package",           preview: "Wrong size, label ready" },
    { title: "Renew car insurance",          preview: "Compare 3 quotes" },
    { title: "Reunion — save the date",      preview: "Weekend cabin, August" },
  ],
  S: [
    { title: "Sunday plans",                 preview: "Brunch, walk, laundry" },
    { title: "Sourdough starter",            preview: "Feed every 12 hours" },
    { title: "School forms",                 preview: "Sign the trip slip" },
    { title: "Sarah's birthday",             preview: "Card + small gift" },
    { title: "Sunday brunch reservation",    preview: "Table for 4, 11:30" },
  ],
  T: [
    { title: "Taxes — gather receipts",      preview: "Utilities, freelance, meds" },
    { title: "Trip to Rome day 1",           preview: "Land 10am, hotel noon" },
    { title: "Tuesday errands",              preview: "Post office, hardware" },
    { title: "Therapy Wednesday 5pm",        preview: "Bring the journal" },
    { title: "Tickets for Friday",           preview: "Row H, on phone + printed" },
  ],
  U: [
    { title: "Uber to airport",              preview: "Book night before, 5:15am" },
    { title: "Utility bill — check meter",   preview: "Estimated seems high" },
    { title: "Umbrella at Sam's",            preview: "Grab it Friday" },
    { title: "Upgrade phone plan",           preview: "More data, same number" },
    { title: "Unpack office boxes",          preview: "Books, decor, cables" },
  ],
  V: [
    { title: "Vet appointment",              preview: "Annual checkup + shots" },
    { title: "Vacation packing",             preview: "Layers, swim, walking shoes" },
    { title: "Visa application",             preview: "Photo, form, bank statement" },
    { title: "Vinyl shopping Saturday",      preview: "The reissue, the live one" },
    { title: "Volunteer sign-up",            preview: "Two Sats a month" },
  ],
  W: [
    { title: "Weekend plans",                preview: "Groceries, run, brunch" },
    { title: "Water bill autopay",           preview: "Set it up this month" },
    { title: "Workout Monday",               preview: "Push day — chest + tris" },
    { title: "Wine pairings Saturday",       preview: "Two whites, one red" },
    { title: "Wedding gift Sam & Alex",      preview: "Registry item + card" },
  ],
  X: [
    { title: "X-ray follow-up",              preview: "Results Wednesday, book call" },
    { title: "Xmas card list",               preview: "Family, friends, work — 24" },
    { title: "Xfinity — call about bill",    preview: "Charge for channel we don't have" },
  ],
};

// ─── DUTCH ──────────────────────────────────────────────────────────
const nl = {
  A: [
    { title: "Afspraak tandarts",            preview: "Woensdag 9:20 — gebitsreiniging" },
    { title: "Auto naar de garage",          preview: "APK volgende week" },
    { title: "Albert Heijn boodschappen",    preview: "Melk, eieren, bananen" },
    { title: "Airport ophalen zaterdag",     preview: "Terminal 3, 18:00" },
    { title: "Anouk verjaardag",             preview: "Kaartje + klein cadeau" },
  ],
  B: [
    { title: "Boekenclub donderdag",         preview: "Hoofdstuk 4-9, bij Maya" },
    { title: "Belastingaangifte",            preview: "Facturen verzamelen" },
    { title: "Boodschappenlijstje",          preview: "Kaas, brood, koffie" },
    { title: "Bank afspraak",                preview: "Paspoort + loonstroken" },
    { title: "Bloemen voor mama",            preview: "Vrijdag ophalen" },
  ],
  C: [
    { title: "Café met Mia",                 preview: "Woensdag, bij haar kantoor" },
    { title: "Cadeau voor Alex",             preview: "Boek + cadeaubon" },
    { title: "Contactlenzen bestellen",      preview: "Zelfde sterkte als vorig jaar" },
    { title: "Cursus week 2",                preview: "Twee video's + quiz" },
    { title: "Concert vrijdag",              preview: "Rij H, plaats 12 & 13" },
  ],
  D: [
    { title: "Dokter afspraak",              preview: "Woensdag 10:00 — recept" },
    { title: "Douchegordijn kopen",          preview: "Xenos of Hema" },
    { title: "Diner reservering",            preview: "Zaterdag 20:00, tafel voor 4" },
    { title: "Dagje uit met kids",           preview: "Efteling zondag, poncho mee" },
    { title: "Deurmat vervangen",            preview: "Nieuwe bij Praxis" },
  ],
  E: [
    { title: "Email Sam beantwoorden",       preview: "Re: factuur vorige maand" },
    { title: "Energie contract vernieuwen",  preview: "Vergelijk 3 aanbieders" },
    { title: "Eten bij ouders",              preview: "Zondag 17:00, wijn mee" },
    { title: "Etentje reserveren",           preview: "Vrijdag 19:30" },
    { title: "Emma dansuitvoering",          preview: "Zaterdag 19:00" },
  ],
  F: [
    { title: "Fietsband plakken",            preview: "Achterband, lekt langzaam" },
    { title: "Feestje zaterdag",             preview: "Cadeau + kaart klaarleggen" },
    { title: "Factuur #204 versturen",       preview: "Einde van de maand" },
    { title: "Fysio afspraak",               preview: "Dinsdag 15:30" },
    { title: "Film avond vrijdag",           preview: "Popcorn + de nieuwe" },
  ],
  G: [
    { title: "Groenteboer zaterdag",         preview: "Tomaten, spinazie, ui" },
    { title: "Gym dinsdag",                  preview: "Benen + core, 45 min" },
    { title: "Geld overmaken huur",          preview: "Voor de 1e" },
    { title: "Grasmaaien zondag",            preview: "Als het droog blijft" },
    { title: "Gitaar snaren vervangen",      preview: "Elixir 10s zoals altijd" },
  ],
  H: [
    { title: "Haar knippen vrijdag",         preview: "Zelfde lengte, iets korter" },
    { title: "Huur overmaken",               preview: "1e van de maand" },
    { title: "Hond uitlaten schema",         preview: "Sleutel onder mat" },
    { title: "Huisarts terugbellen",         preview: "Over het nieuwe recept" },
    { title: "Hobbywinkel lijstje",          preview: "Verf, kwasten, doek" },
  ],
  I: [
    { title: "IKEA ritje",                   preview: "Kast, potten, gehaktballen" },
    { title: "Inpaklijst weekendje",         preview: "Paspoort, lader, medicijnen" },
    { title: "Inschrijven yoga",             preview: "Zaterdag 9:00 — voor donderdag" },
    { title: "Idee cadeau Piet",             preview: "Fles wijn of boek" },
    { title: "Instellingen router",          preview: "Wachtwoord opnieuw zetten" },
  ],
  J: [
    { title: "Jas ophalen stomerij",         preview: "Donderdag klaar" },
    { title: "Jasper verjaardag",            preview: "Zaterdag 15:00, taart mee" },
    { title: "Jaarplanning werk",            preview: "Q1 doelen doornemen" },
    { title: "Juwelier ring inleveren",      preview: "Steentje zit los" },
    { title: "Jamie sollicitatie",           preview: "CV bijwerken, tips" },
  ],
  K: [
    { title: "Kinderen ophalen school",      preview: "Woensdag vroeg — 14:15" },
    { title: "Kapper vrijdag 18:00",         preview: "Zelfde stijl als vorige keer" },
    { title: "Keuken schoonmaken",           preview: "Kastjes leeg, afvegen" },
    { title: "Kaartje sturen oma",           preview: "Haar 82e verjaardag" },
    { title: "Klusjes zaterdag",             preview: "Kraan, plank, gordijn" },
  ],
  L: [
    { title: "Lunch met mama",               preview: "Woensdag 12:30, op de hoek" },
    { title: "Lampjes ophangen",             preview: "Batterijen check" },
    { title: "Loodgieter bellen",            preview: "Lekkage badkamer" },
    { title: "Lidl boodschappen",            preview: "Aanbieding notenmix" },
    { title: "Laten zien nieuwe collega",    preview: "Rondleiding maandag" },
  ],
  M: [
    { title: "Moeders verjaardag",           preview: "14 mei — bloemen + bellen" },
    { title: "Meeting Q3 review",            preview: "Cijfers 12% omhoog" },
    { title: "Marathon training week 6",     preview: "Lange loop zaterdag" },
    { title: "Meubels ophalen zaterdag",     preview: "Bestelbus geregeld" },
    { title: "Melding bij gemeente",         preview: "Verhuizing doorgeven" },
  ],
  N: [
    { title: "Nieuwe telefoon abonnement",   preview: "Meer data, zelfde nummer" },
    { title: "Naaimachine ophalen",          preview: "Reparatie klaar donderdag" },
    { title: "Neef verjaardag",              preview: "Kaartje + tikkie" },
    { title: "Nagels vrijdag",               preview: "Zelfde als vorige keer" },
    { title: "Notitieboek kopen",            preview: "A5, dotted, zachte kaft" },
  ],
  O: [
    { title: "Ouderavond woensdag",          preview: "19:30 aula, formulier mee" },
    { title: "Opa bezoeken zondag",          preview: "Bloemen + koekjes" },
    { title: "Overmaken huur",               preview: "1e — automatisch" },
    { title: "Olie verversen auto",          preview: "Lampje brandt al 200 km" },
    { title: "Oppas vragen zaterdag",        preview: "Vanaf 19:00 tot 23:00" },
  ],
  P: [
    { title: "Paspoort verlengen",           preview: "Foto + afspraak gemeente" },
    { title: "Pilates zaterdag 9:00",        preview: "Boeken voor donderdag" },
    { title: "Post ophalen",                 preview: "Pakketpunt Bruna" },
    { title: "Pizza avond vrijdag",          preview: "Deeg maken donderdag" },
    { title: "Presentatie voorbereiden",     preview: "Verhaal + één groot getal" },
  ],
  Q: [
    { title: "Quiz avond donderdag",         preview: "Team van 4 — nog één zoeken" },
    { title: "Q1 doelen check",              preview: "Twee op koers, één achter" },
    { title: "Quinoa salade recept",         preview: "Feta, granaatappel, munt" },
    { title: "Quality time met kids",        preview: "Zaterdag geen telefoon" },
  ],
  R: [
    { title: "Rijbewijs verlengen",          preview: "Foto + gezondheidsverklaring" },
    { title: "Recept risotto",               preview: "Bouillon, parmezaan, wijn" },
    { title: "Regenjas ophalen",             preview: "Sam's huis, vrijdag" },
    { title: "Rekening betalen energie",     preview: "Voor het einde van de maand" },
    { title: "Reisje boeken augustus",       preview: "Twee weken kust" },
  ],
  S: [
    { title: "Supermarkt zaterdag",          preview: "Broodjes, salade, ijs" },
    { title: "Sportschool dinsdag",          preview: "Push day — borst + triceps" },
    { title: "Schoolformulier tekenen",      preview: "Toestemming schoolreis" },
    { title: "Sam's verjaardag",             preview: "Kaartje + cadeau" },
    { title: "Servies retour Ikea",          preview: "Bonnetje in de tas" },
  ],
  T: [
    { title: "Tandarts controle",            preview: "Woensdag 9:20" },
    { title: "Tuinstoelen schoonmaken",      preview: "Voor de zomer" },
    { title: "Trein naar Utrecht",           preview: "Vrijdag 8:45 vanaf CS" },
    { title: "Telefoon nabellen bank",       preview: "Vraag over hypotheek" },
    { title: "Theater kaarten vrijdag",      preview: "Rij H, stoel 12 & 13" },
  ],
  U: [
    { title: "Uber naar Schiphol",           preview: "Nacht ervoor boeken, 5:15" },
    { title: "Uitnodiging bruiloft",         preview: "Rsvp voor vrijdag" },
    { title: "Uitprinten tickets",           preview: "Vlucht + verzekering" },
    { title: "Universiteit open dag",        preview: "Zaterdag, formulier meenemen" },
    { title: "Uploaden foto's cloud",        preview: "Backup vakantie" },
  ],
  V: [
    { title: "Verjaardag mama",              preview: "14 mei — bloemen + bellen" },
    { title: "Vakantie inpakken",            preview: "Zonnebrand, boek, adapter" },
    { title: "Verzekering vernieuwen",       preview: "Auto — voor vrijdag" },
    { title: "Vrijwilligerswerk zaterdag",   preview: "Twee zaterdagen per maand" },
    { title: "Visite tante zondag",          preview: "Bloemen + koekjes" },
  ],
  W: [
    { title: "Weekend planning",             preview: "Boodschappen, hardlopen, brunch" },
    { title: "Waterrekening automatisch",    preview: "Instellen deze maand" },
    { title: "Wijnkelder aanvullen",         preview: "Twee wit, één rood" },
    { title: "Wasje draaien zondag",         preview: "Handdoeken + lakens" },
    { title: "Werkoverleg maandag",          preview: "Q3 cijfers doornemen" },
  ],
  X: [
    { title: "X-ray uitslag arts",           preview: "Woensdag terugbellen" },
    { title: "Xmas kaartenlijst",            preview: "Familie, vrienden, werk" },
    { title: "Xenos boodschappen",           preview: "Kaarsen + servetten" },
  ],
  Z: [
    { title: "Zwembad zaterdag 9:00",        preview: "Voor de kinderen" },
    { title: "Zonnebrand kopen",             preview: "Factor 50 voor Emma" },
    { title: "Zussen etentje",               preview: "Vrijdag 19:30 bij Anouk" },
    { title: "Ziekenhuis controle",          preview: "Woensdag 10:00" },
    { title: "Zolder opruimen zaterdag",     preview: "Oude dozen weg" },
  ],
};

// ─── SPANISH ────────────────────────────────────────────────────────
const es = {
  A: [
    { title: "Aeropuerto sábado 18h",        preview: "Terminal 2 llegadas" },
    { title: "Agua mineral pedido",          preview: "Caja de 12, para el jueves" },
    { title: "Amazon devoluciones",          preview: "Zapatillas + funda móvil" },
    { title: "Almuerzo con Mia",             preview: "Miércoles cerca oficina" },
    { title: "Alquiler — pagar día 1",       preview: "Domiciliación puesta" },
  ],
  B: [
    { title: "Barbero martes 17:30",         preview: "Igual que la última vez" },
    { title: "Boda de Sam — regalo",         preview: "Lista de bodas + tarjeta" },
    { title: "Banco — cita",                 preview: "DNI + últimas 3 nóminas" },
    { title: "Bicicleta al taller",          preview: "Rueda trasera pierde aire" },
    { title: "Barbacoa sábado",              preview: "Pan, ensalada, hielo" },
  ],
  C: [
    { title: "Cita dentista miércoles",      preview: "9:20 — limpieza + radiografía" },
    { title: "Cena reserva sábado",          preview: "Mesa para 4, 20:00" },
    { title: "Cumpleaños de Sara",           preview: "Tarjeta + regalo pequeño" },
    { title: "Coche a la ITV",               preview: "Cita el jueves 10:00" },
    { title: "Colegio recoger niños",        preview: "Miércoles 14:15" },
  ],
  D: [
    { title: "Dentista miércoles",           preview: "Revisión anual" },
    { title: "Devolver paquete Amazon",      preview: "Etiqueta ya impresa" },
    { title: "Documentos hipoteca",          preview: "Escanear + enviar al banco" },
    { title: "Domingo comida familia",       preview: "Llevar postre" },
    { title: "Deberes con la niña",          preview: "Matemáticas y lengua" },
  ],
  E: [
    { title: "Email Sam devolver",           preview: "Sobre la factura pasada" },
    { title: "Electricidad — recibo",        preview: "Domiciliación puesta" },
    { title: "Envío paquete correos",        preview: "Antes del viernes" },
    { title: "Estanco tabaco abuelo",        preview: "Marca de siempre" },
    { title: "Escuela reunión padres",       preview: "Miércoles 19:30" },
  ],
  F: [
    { title: "Farmacia recoger receta",      preview: "Vitamina D + tiroides" },
    { title: "Factura #204 enviar",          preview: "Fin de mes, 30 días" },
    { title: "Fiesta cumple sábado",         preview: "Confirmar asistencia" },
    { title: "Fisio martes 15:30",           preview: "Espalda + cervicales" },
    { title: "Frutería sábado",              preview: "Tomate, plátano, aguacate" },
  ],
  G: [
    { title: "Gimnasio martes",              preview: "Piernas + core, 45 min" },
    { title: "Gasolina depósito",            preview: "Antes del viaje del viernes" },
    { title: "Gafas nueva graduación",       preview: "Pedir online" },
    { title: "Guitarra cuerdas nuevas",      preview: "Elixir 10s como siempre" },
    { title: "Guardería pagar mes",          preview: "Antes del día 5" },
  ],
  H: [
    { title: "Hipoteca revisión",            preview: "Comparar 3 bancos" },
    { title: "Habitación de Emma",           preview: "Pintar antes del sábado" },
    { title: "Hotel Roma reservar",          preview: "Cerca del centro" },
    { title: "Hospital seguimiento",         preview: "Preguntar por la receta" },
    { title: "Herramientas Leroy Merlin",    preview: "Tornillos + lija" },
  ],
  I: [
    { title: "IKEA sábado",                  preview: "Estantería + macetas" },
    { title: "Impuestos recibos",            preview: "Luz, freelance, médico" },
    { title: "Invitados sábado noche",       preview: "Confirmar 4 personas" },
    { title: "Idea regalo Alex",             preview: "Libro + tarjeta" },
    { title: "Inscripción curso online",     preview: "Semana 2 — dos vídeos" },
  ],
  J: [
    { title: "Jamón cortar sábado",          preview: "Pedir soporte al bar" },
    { title: "Juguetes limpiar sótano",      preview: "Donar los que no usan" },
    { title: "Jardinero martes",             preview: "Cortar seto + regar" },
    { title: "Jueves reunión trabajo",       preview: "Sala 3 — llevar portátil" },
    { title: "Julio vacaciones planear",     preview: "Costa preferida" },
  ],
  K: [
    { title: "Kilos comprobar báscula",      preview: "Después del gimnasio" },
    { title: "Karaoke viernes con Sam",      preview: "Reservar sala" },
    { title: "Kit primeros auxilios",        preview: "Revisar caducidades" },
    { title: "Kayak alquilar domingo",       preview: "Dos horas, chaleco incluido" },
  ],
  L: [
    { title: "Lavandería domingo",           preview: "Sábanas + toallas" },
    { title: "Luz recibo autopago",          preview: "Configurar este mes" },
    { title: "Lentejas domingo comida",      preview: "Con chorizo, para 4" },
    { title: "Libro biblioteca devolver",    preview: "Antes del martes" },
    { title: "Llevar el coche a lavar",      preview: "Sábado por la mañana" },
  ],
  M: [
    { title: "Mamá cumpleaños",              preview: "14 mayo — flores + llamada" },
    { title: "Médico control anual",         preview: "Pedir cita este mes" },
    { title: "Muebles Wallapop vender",      preview: "Sillón + mesita" },
    { title: "Mercadona sábado",             preview: "Semanal — lista larga" },
    { title: "Marta enviar audio",           preview: "Sobre el viaje de agosto" },
  ],
  N: [
    { title: "Notaría documentos",           preview: "Firmar poder — jueves" },
    { title: "Nevera limpiar viernes",       preview: "Sacar todo, revisar fechas" },
    { title: "Nietos fin de semana",         preview: "Vienen sábado tarde" },
    { title: "Nadar martes",                 preview: "Reservar calle 7:30" },
    { title: "Nueva tarjeta bancaria",       preview: "Activar antes del viernes" },
  ],
  O: [
    { title: "Oftalmólogo cita",             preview: "Viernes 16:00, llevar gafas" },
    { title: "Optica nueva graduación",      preview: "Pedir online" },
    { title: "Oficina mudanza fechas",       preview: "Martes muebles, jueves red" },
    { title: "Ordenar armario",              preview: "Sacar ropa de invierno" },
    { title: "Odontólogo revisión",          preview: "Miércoles 9:20" },
  ],
  P: [
    { title: "Paquete correos recoger",      preview: "Antes del viernes" },
    { title: "Pilates sábado 9:00",          preview: "Reservar antes del jueves" },
    { title: "Padres — llamar",              preview: "Domingo mediodía" },
    { title: "Panadería sábado",             preview: "Pan integral + baguette" },
    { title: "Pañales encargar",             preview: "Talla 4, caja grande" },
  ],
  Q: [
    { title: "Quesos comprar viernes",       preview: "Manchego + brie + roquefort" },
    { title: "Quejas comunidad reunión",     preview: "Jueves 20:00 portal" },
    { title: "Química deberes con niño",     preview: "Tabla periódica repasar" },
    { title: "Quitar manchas alfombra",      preview: "Producto del súper" },
  ],
  R: [
    { title: "Renfe billete viernes",        preview: "AVE Madrid 8:45" },
    { title: "Ropa tintorería",              preview: "Traje + dos camisas" },
    { title: "Reunión trabajo miércoles",    preview: "Sala 3, 10:00" },
    { title: "Reservar restaurante sábado",  preview: "Mesa 4 personas 20:30" },
    { title: "Revisión coche taller",        preview: "Jueves 10:00" },
  ],
  S: [
    { title: "Supermercado sábado",          preview: "Semanal — lista larga" },
    { title: "Sábado cumpleaños Sara",       preview: "Tarjeta + regalito" },
    { title: "Seguro coche renovar",         preview: "Comparar 3 pólizas" },
    { title: "Sofá tapicero llamar",         preview: "Presupuesto reforma" },
    { title: "Sábanas cambiar domingo",      preview: "Y toallas del baño" },
  ],
  T: [
    { title: "Taller coche jueves",          preview: "Cambio aceite + filtros" },
    { title: "Tarjeta banco nueva",          preview: "Activar antes del viernes" },
    { title: "Terapia miércoles 17:00",      preview: "Llevar el cuaderno" },
    { title: "Teatro entradas viernes",      preview: "Fila H, butacas 12 y 13" },
    { title: "Tijera peluquería viernes",    preview: "Corte + tinte" },
  ],
  U: [
    { title: "Uber al aeropuerto",           preview: "Reservar la noche antes" },
    { title: "Universidad papeles",          preview: "Matrícula antes del 15" },
    { title: "Uñas viernes 17:00",           preview: "Igual que la vez anterior" },
    { title: "Urgencia consulta veterinaria",preview: "Perro no come bien" },
    { title: "Utensilios cocina lista",      preview: "Sartén, cuchillo, tabla" },
  ],
  V: [
    { title: "Veterinario cita",             preview: "Revisión anual + vacunas" },
    { title: "Vacaciones agosto plan",       preview: "Dos semanas, costa" },
    { title: "Viernes cena Alex",            preview: "Reservar mesa 20:30" },
    { title: "Visita abuela domingo",        preview: "Llevar bizcocho" },
    { title: "Vuelos Roma reservar",         preview: "Ida 10:00, vuelta 20:00" },
  ],
  W: [
    { title: "Wifi router reiniciar",        preview: "Configuración nueva" },
    { title: "Wallapop vender sofá",         preview: "Fotos + descripción" },
    { title: "Whatsapp Sara devolver",       preview: "Del viaje de agosto" },
    { title: "Whiskey regalo Alex",          preview: "Escocés, marca preferida" },
  ],
  X: [
    { title: "Xbox arreglar mando",          preview: "Batería no dura nada" },
    { title: "Xativa fin de semana",         preview: "Reservar hotel centro" },
  ],
};

// ─── FRENCH ─────────────────────────────────────────────────────────
const fr = {
  A: [
    { title: "Aéroport samedi 18h",          preview: "Terminal 2, arrivées" },
    { title: "Amazon retour colis",          preview: "Chaussures + coque téléphone" },
    { title: "Anniversaire Léa",             preview: "Carte + petit cadeau" },
    { title: "Assurance auto renouveler",    preview: "Comparer 3 devis" },
    { title: "Appartement visite jeudi",     preview: "Justificatifs + garant" },
  ],
  B: [
    { title: "Boulangerie samedi",           preview: "Baguette + croissants" },
    { title: "Banque rendez-vous",           preview: "Passeport + fiches de paie" },
    { title: "Barbe rendez-vous mardi",      preview: "Comme d'habitude" },
    { title: "Boîte aux lettres vider",      preview: "Colis relais aussi" },
    { title: "Belote dimanche chez Marc",    preview: "Apporter bouteille" },
  ],
  C: [
    { title: "Café avec Mia",                preview: "Mercredi près du bureau" },
    { title: "Courses samedi",               preview: "Lait, œufs, bananes, pain" },
    { title: "Cadeau pour Alex",             preview: "Livre + carte" },
    { title: "Coiffeur vendredi 18h",        preview: "Coupe + un peu plus court" },
    { title: "Contrôle technique voiture",   preview: "Prendre RDV jeudi" },
  ],
  D: [
    { title: "Dentiste mercredi",            preview: "9h20 — détartrage" },
    { title: "Déclaration impôts",           preview: "Rassembler les reçus" },
    { title: "Dîner réservation samedi",     preview: "Table 4, 20h" },
    { title: "Docteur suivi",                preview: "Demander nouvelle ordonnance" },
    { title: "Décorer chambre Emma",         preview: "Peinture avant samedi" },
  ],
  E: [
    { title: "Envoyer mail à Sam",           preview: "Re: facture du mois dernier" },
    { title: "Essence plein vendredi",       preview: "Avant le départ" },
    { title: "Emma spectacle danse",         preview: "Samedi 19h, robe bleue" },
    { title: "Ecole réunion parents",        preview: "Mercredi 19h30" },
    { title: "Emmener chien vétérinaire",    preview: "Vaccins annuels" },
  ],
  F: [
    { title: "Facture #204 envoyer",         preview: "Fin de mois, 30 jours" },
    { title: "Farine acheter samedi",        preview: "T65, deux paquets" },
    { title: "Fête vendredi soir",           preview: "Anniversaire Sam" },
    { title: "Ferme cueillette dimanche",    preview: "Fraises + framboises" },
    { title: "Film vendredi soir",           preview: "Popcorn + celui de Sam" },
  ],
  G: [
    { title: "Garage voiture jeudi",         preview: "Vidange + filtres" },
    { title: "Gym mardi soir",               preview: "Jambes + gainage 45 min" },
    { title: "Gâteau dimanche chocolat",     preview: "Sans glaçage cette fois" },
    { title: "Grand-mère appeler dimanche",  preview: "Vers 15h" },
    { title: "Guitare cordes changer",       preview: "Elixir 10 comme toujours" },
  ],
  H: [
    { title: "Hôpital suivi radio",          preview: "Résultats mercredi" },
    { title: "Huile changer voiture",        preview: "Voyant depuis 200 km" },
    { title: "Hôtel Rome réserver",          preview: "Proche du centre" },
    { title: "Hebdo courses samedi",         preview: "Liste sur frigo" },
    { title: "Horaires piscine imprimer",    preview: "Pour la carte de bus" },
  ],
  I: [
    { title: "IKEA samedi",                  preview: "Étagère + pots + boulettes" },
    { title: "Impôts déclaration",           preview: "Rassembler documents" },
    { title: "Invités samedi soir",          preview: "Confirmer 4 personnes" },
    { title: "Idée cadeau Alex",             preview: "Livre + bon Fnac" },
    { title: "Internet box changer",         preview: "Comparer offres" },
  ],
  J: [
    { title: "Jardinier mardi",              preview: "Tailler haie + tondre" },
    { title: "Judo Emma inscription",        preview: "Rentrée septembre" },
    { title: "Jean recycler H&M",            preview: "Vieux jeans dans sac" },
    { title: "Jeudi réunion travail",        preview: "Salle 3, 10h" },
    { title: "Journal résilier",             preview: "Fin de mois" },
  ],
  K: [
    { title: "Karaoké vendredi soir",        preview: "Anniv Sam, réserver salle" },
    { title: "Kilomètres voiture noter",     preview: "Pour l'assurance" },
    { title: "Kiné mardi 17h",               preview: "Séance dos" },
    { title: "Kit premiers secours",         preview: "Vérifier dates" },
  ],
  L: [
    { title: "Lunettes nouvelle ordonnance", preview: "Commander en ligne" },
    { title: "Loyer virer avant le 1er",     preview: "Virement automatique" },
    { title: "Lessive dimanche",             preview: "Draps + serviettes" },
    { title: "Léa spectacle vendredi",       preview: "19h, école" },
    { title: "Livre bibliothèque rendre",    preview: "Avant mardi" },
  ],
  M: [
    { title: "Maman anniversaire",           preview: "14 mai — fleurs + appel" },
    { title: "Médecin RDV lundi",            preview: "Suivi tension" },
    { title: "Marché samedi matin",          preview: "Tomates, fromage, pain" },
    { title: "Maison propre samedi",         preview: "Cuisine + salle de bain" },
    { title: "Menu semaine planifier",       preview: "Batch cooking dimanche" },
  ],
  N: [
    { title: "Notaire signature jeudi",      preview: "Procuration" },
    { title: "Noël cadeaux liste",           preview: "Famille + amis" },
    { title: "Nouveau vélo enfant",          preview: "Comparer 3 modèles" },
    { title: "Nettoyer voiture samedi",      preview: "Intérieur + extérieur" },
    { title: "Nager mardi 7h30",             preview: "Ligne 3 réservée" },
  ],
  O: [
    { title: "Ophtalmologue vendredi 16h",   preview: "Nouvelle ordonnance" },
    { title: "Office tourisme Rome",         preview: "Réserver visites" },
    { title: "Ouverture compte enfants",     preview: "Livret A" },
    { title: "Orage rentrer linge",          preview: "Balcon vidé" },
    { title: "Ordinateur sauvegarde",        preview: "Disque externe" },
  ],
  P: [
    { title: "Passeport renouveler",         preview: "Photo + RDV mairie" },
    { title: "Pharmacie récupérer",          preview: "Vitamine D + thyroïde" },
    { title: "Papa appeler dimanche",        preview: "Vers 15h" },
    { title: "Pilates samedi 9h",            preview: "Réserver avant jeudi" },
    { title: "Poissonnier vendredi",         preview: "Cabillaud + saumon" },
  ],
  Q: [
    { title: "Quinoa acheter bio",           preview: "Pour la salade dimanche" },
    { title: "Quatre saisons pneus",         preview: "Devis garage" },
    { title: "Quitter travail tôt vendredi", preview: "Prévenir chef" },
    { title: "Quart d'heure lecture Emma",   preview: "Avant coucher" },
  ],
  R: [
    { title: "Rendez-vous banque",           preview: "Prêt immo" },
    { title: "Recette risotto champignons",  preview: "Bouillon + parmesan" },
    { title: "Retour colis Amazon",          preview: "Étiquette imprimée" },
    { title: "Réunion parents jeudi",        preview: "19h30 école" },
    { title: "Rentrée scolaire liste",       preview: "Fournitures + cartable" },
  ],
  S: [
    { title: "Supermarché samedi",           preview: "Liste sur frigo" },
    { title: "Sport mardi soir",             preview: "Gainage + haut du corps" },
    { title: "Sam anniversaire dimanche",    preview: "Carte + cadeau" },
    { title: "Sécurité sociale carte",       preview: "Renouveler avant fin mois" },
    { title: "Sortie école mercredi",        preview: "Signer autorisation" },
  ],
  T: [
    { title: "Taxe habitation",              preview: "À payer avant le 15" },
    { title: "Théâtre vendredi",             preview: "Rang H, places 12 et 13" },
    { title: "Traiteur samedi commander",    preview: "Plateau apéro 6 personnes" },
    { title: "Trajet Rome préparer",         preview: "Vol + hôtel + transferts" },
    { title: "Tondeuse à réviser",           preview: "Amener au magasin" },
  ],
  U: [
    { title: "Uber aéroport commander",      preview: "Veille au soir, 5h15" },
    { title: "Université dossier fils",      preview: "Inscription avant le 15" },
    { title: "Ustensiles cuisine liste",     preview: "Poêle + couteau + planche" },
    { title: "Urgent facturer client",       preview: "Avant fin de semaine" },
    { title: "Usine visite scolaire",        preview: "Signer autorisation" },
  ],
  V: [
    { title: "Vétérinaire chien",            preview: "Vaccins annuels" },
    { title: "Vacances août plan",           preview: "Deux semaines côte" },
    { title: "Verres cassés remplacer",      preview: "Ikea samedi" },
    { title: "Visite grand-père dimanche",   preview: "Apporter gâteau" },
    { title: "Voyage Rome billets",          preview: "Réserver hôtel centre" },
  ],
  W: [
    { title: "Weekend courses samedi",       preview: "Marché + supermarché" },
    { title: "Wifi mot de passe changer",    preview: "Nouvelle box" },
    { title: "Whisky cadeau Alex",           preview: "Écossais, marque préférée" },
    { title: "Web hébergement renouveler",   preview: "Avant fin de mois" },
  ],
  X: [
    { title: "Xylophone anniversaire fille", preview: "Idée cadeau Emma" },
    { title: "X-Files revoir saison 4",      preview: "Le soir cette semaine" },
  ],
};

// ─── GERMAN ─────────────────────────────────────────────────────────
const de = {
  A: [
    { title: "Arzttermin Mittwoch",          preview: "9:20 — Vorsorge" },
    { title: "Auto zur Werkstatt",           preview: "TÜV nächste Woche" },
    { title: "Amazon Rücksendung",           preview: "Schuhe + Handyhülle" },
    { title: "Anna Geburtstag",              preview: "Karte + kleines Geschenk" },
    { title: "Abendessen Reservierung",      preview: "Samstag 20 Uhr, Tisch für 4" },
  ],
  B: [
    { title: "Bäckerei Samstag",             preview: "Brötchen + Brot" },
    { title: "Bank Termin",                  preview: "Ausweis + Gehaltsnachweise" },
    { title: "Buchclub Donnerstag",          preview: "Kapitel 4-9, bei Maya" },
    { title: "Blumen für Mama",              preview: "Freitag abholen" },
    { title: "Batterien Küche",              preview: "AA für die Waage" },
  ],
  C: [
    { title: "Café mit Mia",                 preview: "Mittwoch, nähe Büro" },
    { title: "Chef Meeting Donnerstag",      preview: "Q3 Zahlen durchgehen" },
    { title: "Cousine Geburtstag",           preview: "Karte + Überweisung" },
    { title: "Chemikalien Baumarkt",         preview: "Unkrautfrei + Dünger" },
    { title: "Camping Ausrüstung prüfen",    preview: "Zelt + Schlafsack" },
  ],
  D: [
    { title: "Dentist Mittwoch",             preview: "Kontrolle + Reinigung" },
    { title: "Deko Kinderzimmer",            preview: "Vor Samstag streichen" },
    { title: "DHL Paket abholen",            preview: "Bis Freitag beim Kiosk" },
    { title: "Dachrinne reinigen",           preview: "Am Wochenende" },
    { title: "Dokumente Bank scannen",       preview: "Für Kredit" },
  ],
  E: [
    { title: "Email Sam antworten",          preview: "Wegen Rechnung Vormonat" },
    { title: "Einkaufen Samstag",            preview: "Milch, Eier, Bananen" },
    { title: "Elternabend Mittwoch",         preview: "19:30, Aula" },
    { title: "Elektriker anrufen",           preview: "Steckdose Küche kaputt" },
    { title: "Emma Tanzaufführung",          preview: "Samstag 19 Uhr" },
  ],
  F: [
    { title: "Friseur Freitag 18 Uhr",       preview: "Wie letztes Mal" },
    { title: "Fahrrad Reifen flicken",       preview: "Hinten, verliert Luft" },
    { title: "Fahrschule bezahlen",          preview: "Nächste Rate" },
    { title: "Fitnessstudio Dienstag",       preview: "Beine + Rumpf 45 min" },
    { title: "Freitag Kinoabend",            preview: "Popcorn + neuer Film" },
  ],
  G: [
    { title: "Geburtstag Mama",              preview: "14. Mai — Blumen + Anruf" },
    { title: "Garage aufräumen",             preview: "Wochenende, Werkzeug" },
    { title: "Gemüse Markt Samstag",         preview: "Tomaten, Spinat, Zwiebeln" },
    { title: "Geld überweisen Miete",        preview: "Vor dem 1." },
    { title: "Garten Frühjahrsbepflanzung",  preview: "Basilikum, Minze" },
  ],
  H: [
    { title: "Haarschnitt Freitag",          preview: "Etwas kürzer diesmal" },
    { title: "Hausratversicherung",          preview: "3 Angebote vergleichen" },
    { title: "Hund zum Tierarzt",            preview: "Impfungen jährlich" },
    { title: "Hotel Rom buchen",             preview: "Nähe Zentrum" },
    { title: "Handy Vertrag verlängern",     preview: "Mehr Daten, gleiche Nummer" },
  ],
  I: [
    { title: "IKEA Samstag",                 preview: "Regal + Töpfe + Bällchen" },
    { title: "Immobilien Termin",            preview: "Donnerstag 15 Uhr" },
    { title: "Idee Geschenk Alex",           preview: "Buch + Gutschein" },
    { title: "Insekten Balkon Spray",        preview: "Baumarkt" },
    { title: "Internet Anbieter wechseln",   preview: "Preise vergleichen" },
  ],
  J: [
    { title: "Jacke Reinigung",              preview: "Freitag fertig" },
    { title: "Jahresplanung Q1",             preview: "Ziele mit Team" },
    { title: "Julia Hochzeit RSVP",          preview: "Bis Freitag antworten" },
    { title: "Juwelier Ring abgeben",        preview: "Stein locker" },
    { title: "Jonas Fußballtraining",        preview: "Dienstag 17 Uhr" },
  ],
  K: [
    { title: "Kinder abholen Schule",        preview: "Mittwoch früh 14:15" },
    { title: "Küche putzen Samstag",         preview: "Schränke ausräumen" },
    { title: "Karneval Kostüm Emma",         preview: "Prinzessin — Stoff besorgen" },
    { title: "Kfz-Steuer bezahlen",          preview: "Vor Ende des Monats" },
    { title: "Kaffee Bohnen bestellen",      preview: "Zwei Kilo, dunkle Röstung" },
  ],
  L: [
    { title: "Lebensmittel Wochenende",      preview: "Wochenplan, große Liste" },
    { title: "Lidl Samstag Angebot",         preview: "Nüsse + Öl" },
    { title: "Lehrer Gespräch Emma",         preview: "Donnerstag 16 Uhr" },
    { title: "Licht Küche reparieren",       preview: "Elektriker Termin" },
    { title: "Lager Keller aufräumen",       preview: "Wochenende" },
  ],
  M: [
    { title: "Mama Geburtstag",              preview: "14. Mai — Blumen" },
    { title: "Meeting Q3 Review",            preview: "Zahlen 12% besser" },
    { title: "Miete überweisen",             preview: "Vor dem 1." },
    { title: "Marathon Training Woche 6",    preview: "Langer Lauf Samstag" },
    { title: "Möbel abholen Samstag",        preview: "Transporter organisieren" },
  ],
  N: [
    { title: "Notar Termin Donnerstag",      preview: "Vollmacht unterschreiben" },
    { title: "Neuen Ausweis beantragen",     preview: "Foto + Termin Bürgeramt" },
    { title: "Nachbarn BBQ Samstag",         preview: "Getränke mitbringen" },
    { title: "Nagelstudio Freitag",          preview: "Wie letztes Mal" },
    { title: "Netflix Empfehlungen",         preview: "Die neue isländische Serie" },
  ],
  O: [
    { title: "Optiker neue Brille",          preview: "Online bestellen" },
    { title: "Olwechsel Auto",               preview: "Warnleuchte an" },
    { title: "Oma anrufen Sonntag",          preview: "Gegen 15 Uhr" },
    { title: "Orchester Karten Freitag",     preview: "Reihe H, Platz 12 & 13" },
    { title: "Online Kurs Woche 2",          preview: "Zwei Videos + Quiz" },
  ],
  P: [
    { title: "Paket bei Post abholen",       preview: "Bis Freitag" },
    { title: "Pass verlängern",              preview: "Foto + Termin" },
    { title: "Papa anrufen Sonntag",         preview: "Gegen 15 Uhr" },
    { title: "Pilates Samstag 9 Uhr",        preview: "Vor Donnerstag buchen" },
    { title: "Pfand wegbringen",             preview: "Zwei Kisten Keller" },
  ],
  Q: [
    { title: "Quiznight Donnerstag",         preview: "Team von 4 — einer fehlt" },
    { title: "Q1 Ziele Check",               preview: "Zwei on track, eins nicht" },
    { title: "Quittungen Steuer sortieren",  preview: "Vor dem Wochenende" },
    { title: "Quinoa Salat Rezept",          preview: "Feta + Granatapfel" },
  ],
  R: [
    { title: "Rechnung #204 senden",         preview: "Ende Monat, 30 Tage" },
    { title: "Reinigung Anzug abholen",      preview: "Freitag fertig" },
    { title: "Reisepass verlängern",         preview: "Foto + Termin Amt" },
    { title: "Rasen mähen Sonntag",          preview: "Falls trocken" },
    { title: "Rezept vom Arzt",              preview: "Wiederholen lassen" },
  ],
  S: [
    { title: "Supermarkt Samstag",           preview: "Wochenliste am Kühlschrank" },
    { title: "Sport Dienstag",               preview: "Push day — Brust + Trizeps" },
    { title: "Schule Formular unterschreiben",preview: "Ausflug Erlaubnis" },
    { title: "Sarah Geburtstag",             preview: "Karte + kleines Geschenk" },
    { title: "Sonntag Brunch",               preview: "Tisch für 4, 11:30" },
  ],
  T: [
    { title: "Tierarzt Termin",              preview: "Jährliche Kontrolle" },
    { title: "Trockner Filter reinigen",     preview: "Übers Wochenende" },
    { title: "TÜV Auto Termin",              preview: "Vor Monatsende" },
    { title: "Theater Karten Freitag",       preview: "Reihe H, Platz 12 & 13" },
    { title: "Telefon Bank zurückrufen",     preview: "Wegen Hypothek" },
  ],
  U: [
    { title: "Uber zum Flughafen",           preview: "Nacht vorher, 5:15" },
    { title: "Uni Anmeldung Sohn",           preview: "Vor 15. abgeben" },
    { title: "Urlaub August planen",         preview: "Zwei Wochen Küste" },
    { title: "Umzug Kisten packen",          preview: "Bücher + Deko" },
    { title: "Uhr Batterie wechseln",        preview: "Juwelier Kaiserstraße" },
  ],
  V: [
    { title: "Versicherung Auto erneuern",   preview: "3 Angebote vergleichen" },
    { title: "Vater Geburtstag",             preview: "12. Juli — Anruf + Karte" },
    { title: "Vergessen — Rezept einlösen",  preview: "Vitamin D + Schilddrüse" },
    { title: "Veranstaltung Emma Freitag",   preview: "Schule, 19 Uhr" },
    { title: "Volksbank Termin",             preview: "Donnerstag 15 Uhr" },
  ],
  W: [
    { title: "Wochenende Planung",           preview: "Einkauf, Laufen, Brunch" },
    { title: "Wasser Rechnung Autozahlung",  preview: "Diese Woche einrichten" },
    { title: "Werkstatt Termin Freitag",     preview: "Ölwechsel + Filter" },
    { title: "Wäsche Sonntag waschen",       preview: "Bettwäsche + Handtücher" },
    { title: "Wohnzimmer streichen",         preview: "Am Wochenende, hellgrau" },
  ],
  X: [
    { title: "X-Ray Nachuntersuchung",       preview: "Ergebnisse Mittwoch" },
    { title: "Xmas Karten Liste",            preview: "Familie + Freunde" },
    { title: "Xylophon Emma Geburtstag",     preview: "Idee Geschenk" },
  ],
};

// ─── ITALIAN ────────────────────────────────────────────────────────
const it = {
  A: [
    { title: "Appuntamento dentista",        preview: "Mercoledì 9:20" },
    { title: "Auto dal meccanico",           preview: "Tagliando settimana prossima" },
    { title: "Amazon reso pacco",            preview: "Scarpe + custodia" },
    { title: "Anna compleanno",              preview: "Biglietto + regalino" },
    { title: "Aperitivo con Mia",            preview: "Mercoledì vicino ufficio" },
  ],
  B: [
    { title: "Barbiere martedì 17:30",       preview: "Come l'altra volta" },
    { title: "Banca appuntamento",           preview: "Documento + buste paga" },
    { title: "Book club giovedì",            preview: "Capitoli 4-9, da Maya" },
    { title: "Biglietti concerto venerdì",   preview: "Fila H, posti 12 e 13" },
    { title: "Bimbi scuola prendere",        preview: "Mercoledì 14:15" },
  ],
  C: [
    { title: "Cena sabato 20",               preview: "Tavolo 4 persone" },
    { title: "Caffè con Mia",                preview: "Mercoledì vicino ufficio" },
    { title: "Cameretta Emma dipingere",     preview: "Sabato mattina" },
    { title: "Casa pulire sabato",           preview: "Cucina + bagno" },
    { title: "Coop spesa sabato",            preview: "Lista sul frigo" },
  ],
  D: [
    { title: "Dentista mercoledì",           preview: "Igiene + panoramica" },
    { title: "Dichiarazione redditi",        preview: "Raccogliere ricevute" },
    { title: "Domenica pranzo suoceri",      preview: "Portare dolce" },
    { title: "Documenti mutuo scansione",    preview: "Inviare in banca" },
    { title: "Distributore benzina",         preview: "Prima del viaggio" },
  ],
  E: [
    { title: "Email a Sam rispondere",       preview: "Su fattura mese scorso" },
    { title: "Elettricità bolletta",         preview: "Domiciliazione attiva" },
    { title: "Emma saggio danza",            preview: "Sabato 19, vestito blu" },
    { title: "Estetista venerdì",            preview: "Manicure come sempre" },
    { title: "Enel contratto rinnovo",       preview: "Confrontare 3 offerte" },
  ],
  F: [
    { title: "Fattura #204 inviare",         preview: "Fine mese, 30 giorni" },
    { title: "Farmacia ritirare",            preview: "Vitamina D + tiroide" },
    { title: "Feste sabato compleanno",      preview: "Confermare presenza" },
    { title: "Fisioterapia martedì",         preview: "Schiena + cervicali" },
    { title: "Frutta mercato sabato",        preview: "Pomodori, mele, uva" },
  ],
  G: [
    { title: "Ginnastica martedì",           preview: "Gambe + core 45 min" },
    { title: "Gomme invernali cambio",       preview: "Officina prossima settimana" },
    { title: "Gita domenica campagna",       preview: "Portare picnic" },
    { title: "Giardino piante primavera",    preview: "Basilico + menta" },
    { title: "Gelateria dopo cena",          preview: "Sabato sera bimbi" },
  ],
  H: [
    { title: "Hotel Roma prenotare",         preview: "Vicino centro" },
    { title: "Halloween costume Emma",       preview: "Strega — stoffa nera" },
    { title: "Hobby modellismo pezzi",       preview: "Ordinare online" },
    { title: "Hard disk backup",             preview: "Foto vacanze" },
    { title: "Hosting sito rinnovare",       preview: "Prima fine mese" },
  ],
  I: [
    { title: "IKEA sabato",                  preview: "Mensola + vasi + polpette" },
    { title: "Idea regalo Alex",             preview: "Libro + carta regalo" },
    { title: "Impianto acqua controllo",     preview: "Idraulico martedì" },
    { title: "Invitati sabato sera",         preview: "Confermare 4 persone" },
    { title: "INPS documenti figlio",        preview: "Portare in patronato" },
  ],
  J: [
    { title: "Juventus partita domenica",    preview: "Guardare con Marco" },
    { title: "Jeans vecchi H&M ricicla",     preview: "Nel sacco vicino porta" },
    { title: "Judo Emma iscrizione",         preview: "Settembre — modulo" },
    { title: "Jolly cambiare a banca",       preview: "Spiccioli in vaso" },
  ],
  K: [
    { title: "Karaoke venerdì sera",         preview: "Compleanno Sam, prenotare" },
    { title: "Kit primo soccorso auto",      preview: "Controllare scadenze" },
    { title: "Karate Marco palestra",        preview: "Martedì e giovedì" },
    { title: "Kg pesarsi dopo palestra",     preview: "Grafico settimanale" },
  ],
  L: [
    { title: "Lavanderia ritirare",          preview: "Vestito + due camicie" },
    { title: "Libro biblioteca restituire",  preview: "Prima martedì" },
    { title: "Lunedì riunione capo",         preview: "Numeri Q3 rivedere" },
    { title: "Latte comprare Coop",          preview: "Intero, tre bottiglie" },
    { title: "Luce bolletta autopagamento",  preview: "Configurare mese" },
  ],
  M: [
    { title: "Mamma compleanno",             preview: "14 maggio — fiori + telefonata" },
    { title: "Medico controllo annuale",     preview: "Fissare appuntamento" },
    { title: "Mercato sabato mattina",       preview: "Pomodori, formaggio, pane" },
    { title: "Mutuo documenti banca",        preview: "Scansione + invio" },
    { title: "Marta chiamare weekend",       preview: "Del viaggio agosto" },
  ],
  N: [
    { title: "Nonna telefonare domenica",    preview: "Verso le 15" },
    { title: "Notaio firma giovedì",         preview: "Procura fratello" },
    { title: "Nettare pesche marmellata",    preview: "Weekend sciroppo" },
    { title: "Nuova SIM attivare",           preview: "Prima venerdì" },
    { title: "Nuoto martedì 7:30",           preview: "Corsia 3 prenotata" },
  ],
  O: [
    { title: "Ospedale controllo",           preview: "Risultati mercoledì" },
    { title: "Oculista venerdì 16",          preview: "Nuova ricetta" },
    { title: "Officina auto giovedì",        preview: "Cambio olio + filtri" },
    { title: "Occhiali online ordinare",     preview: "Nuova gradazione" },
    { title: "Ombrellone spiaggia comprare", preview: "Prima estate" },
  ],
  P: [
    { title: "Pacco posta ritirare",         preview: "Entro venerdì" },
    { title: "Palestra martedì",             preview: "Gambe + core" },
    { title: "Pasta madre rinfrescare",      preview: "Ogni 12 ore" },
    { title: "Pizzeria venerdì sera",        preview: "Tavolo 5 persone" },
    { title: "Papà chiamare domenica",       preview: "Verso le 15" },
  ],
  Q: [
    { title: "Quaderno Emma comprare",       preview: "A righe, copertina rigida" },
    { title: "Quinoa insalata ricetta",      preview: "Feta + melograno" },
    { title: "Quotidiano disdire",           preview: "Fine mese" },
    { title: "Q1 obiettivi verifica",        preview: "Due sì, uno in ritardo" },
  ],
  R: [
    { title: "Ricetta risotto funghi",       preview: "Brodo + parmigiano" },
    { title: "Riparazione lavastoviglie",    preview: "Tecnico martedì" },
    { title: "Regalo Sam matrimonio",        preview: "Lista nozze + biglietto" },
    { title: "Riunione lavoro giovedì",      preview: "Sala 3, ore 10" },
    { title: "Roma viaggio prenotare",       preview: "Volo + hotel" },
  ],
  S: [
    { title: "Supermercato sabato",          preview: "Lista sul frigo" },
    { title: "Scuola riunione genitori",     preview: "Mercoledì 19:30" },
    { title: "Sarah compleanno",             preview: "Biglietto + regalo" },
    { title: "Sport martedì",                preview: "Palestra + core" },
    { title: "Salumeria sabato",             preview: "Prosciutto + parmigiano" },
  ],
  T: [
    { title: "Tasse ricevute raccogliere",   preview: "Utenze + freelance" },
    { title: "Teatro biglietti venerdì",     preview: "Fila H, posti 12 e 13" },
    { title: "Telefono banca richiamare",    preview: "Su mutuo" },
    { title: "Tintoria ritirare venerdì",    preview: "Abito + due camicie" },
    { title: "Terapia mercoledì 17",         preview: "Portare diario" },
  ],
  U: [
    { title: "Uber aeroporto prenotare",     preview: "Sera prima, 5:15" },
    { title: "Ufficio postale pacco",        preview: "Entro venerdì" },
    { title: "Università figlio iscrizione", preview: "Entro il 15" },
    { title: "Unghie venerdì",               preview: "Come l'altra volta" },
    { title: "Urgente cliente fatturare",    preview: "Prima fine settimana" },
  ],
  V: [
    { title: "Veterinario cane",             preview: "Vaccini annuali" },
    { title: "Vacanze agosto pianificare",   preview: "Due settimane costa" },
    { title: "Venerdì cena Alex",            preview: "Prenotare tavolo 20:30" },
    { title: "Visita nonna domenica",        preview: "Portare torta" },
    { title: "Voli Roma prenotare",          preview: "Andata 10, ritorno 20" },
  ],
  W: [
    { title: "Weekend spesa sabato",         preview: "Mercato + supermercato" },
    { title: "Wifi router riavviare",        preview: "Configurazione nuova" },
    { title: "Whatsapp Marta rispondere",    preview: "Del viaggio agosto" },
    { title: "Whisky regalo Alex",           preview: "Scozzese, preferito" },
  ],
  X: [
    { title: "Xilofono compleanno Emma",     preview: "Idea regalo" },
    { title: "X-ray controllo risultati",    preview: "Mercoledì telefonare" },
  ],
};

// ─── PORTUGUESE ─────────────────────────────────────────────────────
const pt = {
  A: [
    { title: "Aeroporto sábado 18h",         preview: "Terminal 2 chegadas" },
    { title: "Almoço com Mia",               preview: "Quarta perto do escritório" },
    { title: "Amazon devolução",             preview: "Ténis + capa telemóvel" },
    { title: "Ana aniversário",              preview: "Cartão + presente pequeno" },
    { title: "Ambulatório consulta",         preview: "Quarta 10h — receita" },
  ],
  B: [
    { title: "Barbeiro terça 17:30",         preview: "Igual à última vez" },
    { title: "Banco marcar reunião",         preview: "Documentos + recibos" },
    { title: "Bicicleta ao mecânico",        preview: "Roda traseira" },
    { title: "Bolo domingo",                 preview: "Chocolate sem cobertura" },
    { title: "Bebé consulta pediatra",       preview: "Sexta 15h" },
  ],
  C: [
    { title: "Continente sábado compras",    preview: "Lista no frigorífico" },
    { title: "Cabeleireiro sexta 18h",       preview: "Igual — mais curto" },
    { title: "Carro à revisão",              preview: "Óleo + filtros" },
    { title: "Consulta médica quinta",       preview: "Análises resultados" },
    { title: "Casa arrumar sábado",          preview: "Cozinha + casa de banho" },
  ],
  D: [
    { title: "Dentista quarta",              preview: "Limpeza + panorâmica" },
    { title: "Documentos casa scanear",      preview: "Para o banco" },
    { title: "Domingo almoço sogros",        preview: "Levar sobremesa" },
    { title: "Devolver livro biblioteca",    preview: "Antes de terça" },
    { title: "Doutor Silva seguimento",      preview: "Nova receita" },
  ],
  E: [
    { title: "Email Sam responder",          preview: "Sobre fatura mês passado" },
    { title: "Escola reunião pais",          preview: "Quarta 19:30" },
    { title: "Emma dança sábado 19h",        preview: "Vestido azul" },
    { title: "EDP contrato mudar",           preview: "Comparar 3 tarifas" },
    { title: "Encomenda CTT levantar",       preview: "Até sexta" },
  ],
  F: [
    { title: "Fatura #204 enviar",           preview: "Fim do mês, 30 dias" },
    { title: "Farmácia levantar receita",    preview: "Vitamina D + tiroide" },
    { title: "Feriado planear passeio",      preview: "Praia sábado se sol" },
    { title: "Fisioterapia terça",           preview: "Costas + cervical" },
    { title: "Frutaria sábado",              preview: "Tomate, banana, abacate" },
  ],
  G: [
    { title: "Ginásio terça",                preview: "Pernas + core 45 min" },
    { title: "Gasolina abastecer sexta",     preview: "Antes da viagem" },
    { title: "Ginásio Emma piscina",         preview: "Terça 17h" },
    { title: "Guitarra cordas novas",        preview: "Elixir 10 como sempre" },
    { title: "Guarda-fatos arrumar",         preview: "Roupa de inverno" },
  ],
  H: [
    { title: "Hospital seguimento",          preview: "Resultados quarta" },
    { title: "Hotel Roma reservar",          preview: "Perto do centro" },
    { title: "Horário piscina imprimir",     preview: "Para o cartão do metro" },
    { title: "Hipoteca revisão banco",       preview: "Comparar taxas" },
    { title: "Hoje jantar Alex",             preview: "Reservar mesa 20:30" },
  ],
  I: [
    { title: "IKEA sábado ida",              preview: "Estante + vasos + almôndegas" },
    { title: "Impostos recibos juntar",      preview: "Luz, freelance, médico" },
    { title: "Idade Emma médico dentista",   preview: "Consulta pediátrica" },
    { title: "Internet contrato mudar",      preview: "Comparar 3 operadoras" },
    { title: "Ideia presente Alex",          preview: "Livro + vale" },
  ],
  J: [
    { title: "Jantar sábado reservar",       preview: "Mesa 4 pessoas 20h" },
    { title: "Jardineiro terça",             preview: "Cortar sebe + regar" },
    { title: "João aniversário sábado",      preview: "Presente + cartão" },
    { title: "Jornais reciclagem",           preview: "Domingo antes recolha" },
    { title: "Jeans lavar separado",         preview: "Fim de semana" },
  ],
  K: [
    { title: "Karaoke sexta noite",          preview: "Aniversário Sam" },
    { title: "Kit primeiros socorros",       preview: "Verificar validades" },
    { title: "Karate Marco terça",           preview: "Ginásio 18h" },
    { title: "Kg pesar depois ginásio",      preview: "Gráfico semanal" },
  ],
  L: [
    { title: "Lavandaria levantar",          preview: "Fato + duas camisas" },
    { title: "Livro devolver biblioteca",    preview: "Antes de terça" },
    { title: "Luz fatura débito direto",     preview: "Configurar este mês" },
    { title: "Loja móveis sábado",           preview: "Ver sofá cinza" },
    { title: "Léa aniversário domingo",      preview: "Cartão + prenda" },
  ],
  M: [
    { title: "Mãe aniversário",              preview: "14 maio — flores + chamada" },
    { title: "Médico consulta anual",        preview: "Marcar este mês" },
    { title: "Mercado sábado manhã",         preview: "Tomate, queijo, pão" },
    { title: "Mecânico marcar carro",        preview: "Revisão anual" },
    { title: "Marta enviar áudio",           preview: "Sobre viagem agosto" },
  ],
  N: [
    { title: "Notário assinatura quinta",    preview: "Procuração" },
    { title: "Neve pneus mudar",             preview: "Antes do inverno" },
    { title: "Netflix filmes lista",         preview: "Nova série islandesa" },
    { title: "Nadar terça 7:30",             preview: "Pista 3 reservada" },
    { title: "Neta buscar escola quarta",    preview: "14:15 saída" },
  ],
  O: [
    { title: "Oftalmologista sexta 16h",     preview: "Nova receita" },
    { title: "Optica encomendar online",     preview: "Nova graduação" },
    { title: "Oleo carro trocar",            preview: "Luz aviso há 200 km" },
    { title: "Ordenado transferir poupança", preview: "Automaticamente dia 25" },
    { title: "Ouvir podcast viagem",         preview: "Baixar antes sexta" },
  ],
  P: [
    { title: "Padaria sábado manhã",         preview: "Pão + croissants" },
    { title: "Pagar renda dia 1",            preview: "Transferência automática" },
    { title: "Pai chamar domingo",           preview: "Cerca das 15h" },
    { title: "Pilates sábado 9h",            preview: "Marcar antes quinta" },
    { title: "Passaporte renovar",           preview: "Foto + marcação" },
  ],
  Q: [
    { title: "Queijo comprar sábado",        preview: "Serra + brie" },
    { title: "Quarta reunião chefe",         preview: "Números Q3" },
    { title: "Quintal limpar sábado",        preview: "Folhas + cortar relva" },
    { title: "Quilos pesar semanalmente",    preview: "Depois do ginásio" },
  ],
  R: [
    { title: "Receita risoto cogumelos",     preview: "Caldo + parmesão" },
    { title: "Renda pagar dia 1",            preview: "Transferência automática" },
    { title: "Reunião trabalho quinta",      preview: "Sala 3, 10h" },
    { title: "Reservar restaurante sábado",  preview: "Mesa 4 pessoas 20:30" },
    { title: "Roma viagem reservar",         preview: "Voo + hotel" },
  ],
  S: [
    { title: "Supermercado sábado",          preview: "Lista no frigorífico" },
    { title: "Sara aniversário",             preview: "Cartão + presente" },
    { title: "Sábado brunch reserva",        preview: "Mesa 4, 11:30" },
    { title: "Seguro carro renovar",         preview: "Comparar 3 seguros" },
    { title: "Sofá aspirar sábado",          preview: "Almofadas também" },
  ],
  T: [
    { title: "Taxa condomínio pagar",        preview: "Até dia 10" },
    { title: "Teatro bilhetes sexta",        preview: "Fila H, lugares 12 e 13" },
    { title: "Terapia quarta 17h",           preview: "Levar caderno" },
    { title: "Tinta parede sala comprar",    preview: "Cinza claro Leroy Merlin" },
    { title: "Trocar lâmpada cozinha",       preview: "LED quente" },
  ],
  U: [
    { title: "Uber aeroporto marcar",        preview: "Noite antes, 5:15" },
    { title: "Unhas sexta 17h",              preview: "Igual à última vez" },
    { title: "Universidade filho matrícula", preview: "Antes dia 15" },
    { title: "Urgente cliente faturar",      preview: "Fim da semana" },
    { title: "Utensílios cozinha lista",     preview: "Frigideira + faca" },
  ],
  V: [
    { title: "Veterinário cão",              preview: "Vacinas anuais" },
    { title: "Viagem agosto planear",        preview: "Duas semanas costa" },
    { title: "Visitar avó domingo",          preview: "Levar bolo" },
    { title: "Voo Roma marcar",              preview: "Ida 10h volta 20h" },
    { title: "Vinho comprar sábado",         preview: "Dois brancos, um tinto" },
  ],
  W: [
    { title: "Wifi router reiniciar",        preview: "Configuração nova" },
    { title: "Whatsapp Marta responder",     preview: "Sobre viagem agosto" },
    { title: "Whisky presente Alex",         preview: "Escocês preferido" },
    { title: "Wallapop vender sofá",         preview: "Fotos + descrição" },
  ],
  X: [
    { title: "Xarope tosse Emma",            preview: "Farmácia antes noite" },
    { title: "X-ray resultados médico",      preview: "Quarta chamar" },
  ],
};

// ─── COMPACT LATIN LANGUAGES ────────────────────────────────────────
// The remaining Latin-script languages (pl, sv, no, da, fi, cs, ro,
// hu, tr, id) each get 3 concise entries per letter. Kept terse but
// natural so long lists don't repeat.

const pl = {
  A: [{title:"Apteka odbiór recepty",preview:"Witamina D + tarczyca"},{title:"Auto do warsztatu",preview:"Wymiana oleju"},{title:"Aleksandra urodziny",preview:"Kartka + prezent"}],
  B: [{title:"Bank spotkanie",preview:"Dowód + trzy odcinki"},{title:"Basen wtorek 7:30",preview:"Tor 3 zarezerwowany"},{title:"Biedronka sobota",preview:"Lista na lodówce"}],
  C: [{title:"Ciasto niedziela",preview:"Czekoladowe, bez lukru"},{title:"Cukiernia sobota",preview:"Rogaliki + chleb"},{title:"Ciotka telefon niedziela",preview:"Około 15:00"}],
  D: [{title:"Dentysta środa 9:20",preview:"Higiena + prześwietlenie"},{title:"Dokumenty do banku",preview:"Skan + wysłać"},{title:"Dziadek odwiedzić niedziela",preview:"Przynieść ciasto"}],
  E: [{title:"Email do Sama odpisać",preview:"O fakturze zeszłego miesiąca"},{title:"Elektryk zadzwonić",preview:"Gniazdko w kuchni"},{title:"Emma występ taneczny",preview:"Sobota 19, granatowa sukienka"}],
  F: [{title:"Faktura #204 wysłać",preview:"Koniec miesiąca, 30 dni"},{title:"Fryzjer piątek 18",preview:"Jak ostatnio, krócej"},{title:"Fotel do renowacji",preview:"Tapicer wtorek"}],
  G: [{title:"Groszek zamrożony kupić",preview:"Do zupy niedzielnej"},{title:"Gym wtorek",preview:"Nogi + core 45 min"},{title:"Gramofon igła nowa",preview:"Zamówić online"}],
  H: [{title:"Hipoteka wizyta w banku",preview:"Porównać 3 oferty"},{title:"Herbaty kupić sobota",preview:"Zielona + rooibos"},{title:"Hotel Rzym rezerwacja",preview:"Blisko centrum"}],
  I: [{title:"IKEA sobota",preview:"Regał + doniczki + klopsiki"},{title:"Imieniny Anny",preview:"Kartka + drobiazg"},{title:"Internet zmiana operatora",preview:"Porównać oferty"}],
  J: [{title:"Jajka kupić 30 sztuk",preview:"Do ciast wielkanocnych"},{title:"Jacek pożyczyć wiertarkę",preview:"Sobota rano"},{title:"Jesienne opony wymienić",preview:"Warsztat piątek"}],
  K: [{title:"Kalendarz kupić nowy",preview:"Z tygodniowym widokiem"},{title:"Kwiaty dla mamy",preview:"Piątek odebrać"},{title:"Kot do weterynarza",preview:"Szczepienia roczne"}],
  L: [{title:"Lidl sobota promocja",preview:"Orzechy + oliwa"},{title:"Lekarz kontrola roczna",preview:"Umówić się w tym miesiącu"},{title:"Lista prezentów świątecznych",preview:"Rodzina + praca"}],
  M: [{title:"Mama urodziny 14 maja",preview:"Kwiaty + telefon"},{title:"Mieszkanie oglądanie czwartek",preview:"Zaświadczenie o dochodach"},{title:"Meeting Q3 wyniki",preview:"12% w górę"}],
  N: [{title:"Notariusz podpis czwartek",preview:"Pełnomocnictwo"},{title:"Nowe zdjęcie do dowodu",preview:"Fotobudka na dworcu"},{title:"Naprawa pralki wezwać",preview:"Nie odwirowuje"}],
  O: [{title:"Okulista piątek 16",preview:"Nowa recepta"},{title:"Opłata za czynsz 1.",preview:"Zlecenie stałe"},{title:"Ogród wiosenne sadzenie",preview:"Bazylia + mięta"}],
  P: [{title:"Paszport wymiana",preview:"Foto + urząd"},{title:"Prezent dla Ani",preview:"Książka + kartka"},{title:"Pilates sobota 9",preview:"Zarezerwować do czwartku"}],
  Q: [{title:"Quinoa sałatka niedziela",preview:"Feta + granat"},{title:"Quiz w pubie czwartek",preview:"Drużyna 4 — brakuje jednego"}],
  R: [{title:"Recepta powtórna od lekarza",preview:"Zadzwonić do przychodni"},{title:"Rachunek za prąd",preview:"Zlecenie stałe"},{title:"Roma wyjazd rezerwacja",preview:"Lot + hotel"}],
  S: [{title:"Sklep sobota duże zakupy",preview:"Lista na lodówce"},{title:"Siłownia wtorek",preview:"Push day — klatka + tricepsy"},{title:"Szkoła zebranie środa 19:30",preview:"Podpisać zgodę"}],
  T: [{title:"Tata zadzwonić niedziela",preview:"Około 15:00"},{title:"Teatr piątek bilety",preview:"Rząd H, miejsca 12 i 13"},{title:"Terapia środa 17",preview:"Zabrać dziennik"}],
  U: [{title:"Uber na lotnisko",preview:"Zamówić dzień wcześniej"},{title:"Urodziny Sary",preview:"Prezent + kartka"},{title:"Umowa energii porównać",preview:"Trzy oferty"}],
  V: [{title:"Vitamina D kupić",preview:"Apteka + tarczyca"},{title:"Voucher spa dla mamy",preview:"Prezent urodzinowy"},{title:"Video z komunii wysłać",preview:"Chmura link"}],
  W: [{title:"Weekend zakupy sobota",preview:"Rynek + market"},{title:"Woda rachunek automatyczny",preview:"Skonfigurować"},{title:"Warsztat auto piątek",preview:"Wymiana oleju"}],
  X: [{title:"Xbox pad naprawić",preview:"Bateria nie trzyma"}],
};

const sv = {
  A: [{title:"Apotek hämta recept",preview:"Vitamin D + sköldkörtel"},{title:"Aktivering nya kortet",preview:"Innan fredag"},{title:"Anna födelsedag",preview:"Kort + present"}],
  B: [{title:"Bank möte torsdag",preview:"Id + lönebesked"},{title:"Bageri lördag morgon",preview:"Bröd + kanelbullar"},{title:"Barn hämta skola",preview:"Onsdag 14:15"}],
  C: [{title:"Coop lördag storhandling",preview:"Lista på kylen"},{title:"Cykel däck laga",preview:"Bakhjul läcker"},{title:"Café med Mia onsdag",preview:"Vid hennes kontor"}],
  D: [{title:"Dentist onsdag 9:20",preview:"Undersökning + tandsten"},{title:"Diskmaskin reparation",preview:"Ringa tekniker"},{title:"Dokument bank skanna",preview:"Skickas i morgon"}],
  E: [{title:"E-post Sam svara",preview:"Ang. förra månadens faktura"},{title:"Emma dansuppvisning",preview:"Lördag 19, blå klänning"},{title:"El-avtal förnya",preview:"Jämför tre bolag"}],
  F: [{title:"Frisör fredag 18",preview:"Som förra gången"},{title:"Faktura #204 skicka",preview:"Månadens slut, 30 dagar"},{title:"Fotbollsträning Emma",preview:"Tisdag 17"}],
  G: [{title:"Gym tisdag",preview:"Ben + core 45 min"},{title:"Grönsaker torg lördag",preview:"Tomat + spenat"},{title:"Gitarr strängar byte",preview:"Elixir 10 som vanligt"}],
  H: [{title:"Hyra betala den 1:a",preview:"Autogiro klart"},{title:"Hår klippa fredag",preview:"Kortare denna gång"},{title:"Hund veterinär",preview:"Årsvaccin"}],
  I: [{title:"IKEA lördag",preview:"Hylla + krukor + köttbullar"},{title:"Idé present Alex",preview:"Bok + presentkort"},{title:"Internet leverantör byte",preview:"Jämför pris"}],
  J: [{title:"Jobbmöte torsdag",preview:"Rum 3, kl 10"},{title:"Jul-kort lista",preview:"Familj + vänner"},{title:"Julia födelsedag",preview:"Kort + present"}],
  K: [{title:"Klippa gräset söndag",preview:"Om det är torrt"},{title:"Kaffe bönor beställa",preview:"Två kilo mörkrost"},{title:"Kaffe med Mia onsdag",preview:"Nära hennes jobb"}],
  L: [{title:"Löneutbetalning kolla",preview:"Ska komma 25:e"},{title:"Läkare årlig kontroll",preview:"Boka i månaden"},{title:"Lena ringa söndag",preview:"Om semesterresan"}],
  M: [{title:"Mamma födelsedag",preview:"14 maj — blommor + ring"},{title:"Möte Q3 genomgång",preview:"Siffror upp 12%"},{title:"Matlagning söndag",preview:"Veckans luncher"}],
  N: [{title:"Notarius kontrakt",preview:"Torsdag 15"},{title:"Nya glasögon beställa",preview:"Nytt recept"},{title:"Netflix serie lista",preview:"Isländska deckaren"}],
  O: [{title:"Optiker fredag 16",preview:"Nytt recept glasögon"},{title:"Olja bilbyte",preview:"Servicelampa har lyst"},{title:"Ombokning tandläkare",preview:"Ringa reception"}],
  P: [{title:"Paket hämta ICA",preview:"Innan fredag"},{title:"Pass förnya",preview:"Foto + tid polisen"},{title:"Pappa ringa söndag",preview:"Runt 15"}],
  Q: [{title:"Quinoa sallad söndag",preview:"Fetaost + granatäpple"},{title:"Quiz-kväll torsdag",preview:"Team 4 — behöver en till"}],
  R: [{title:"Räkning el autogiro",preview:"Sätta upp denna månad"},{title:"Recept svamprisotto",preview:"Buljong + parmesan"},{title:"Reservation restaurang lördag",preview:"Bord 4, 20:30"}],
  S: [{title:"Systembolaget fredag",preview:"Två vita, en röd"},{title:"Sport tisdag",preview:"Push day — bröst + triceps"},{title:"Sara födelsedag",preview:"Kort + present"}],
  T: [{title:"Tandläkare onsdag",preview:"Undersökning + tandsten"},{title:"Teater fredag biljetter",preview:"Rad H, plats 12 & 13"},{title:"Tvätt söndag",preview:"Lakan + handdukar"}],
  U: [{title:"Uber flygplats boka",preview:"Kväll innan, 5:15"},{title:"Universitet ansökan barn",preview:"Innan 15:e"},{title:"Underhållningsapp barn",preview:"Ladda ner inför resan"}],
  V: [{title:"Veterinär hund",preview:"Årsvaccin"},{title:"Vecka 6 marathonträning",preview:"Långlöp lördag"},{title:"Vin fredag två flaskor",preview:"En vit, en röd"}],
  W: [{title:"Weekend planera lördag",preview:"Handla, springa, brunch"},{title:"Wifi router omstart",preview:"Ny konfiguration"},{title:"Whisky present Alex",preview:"Skotsk, favoritmärket"}],
  X: [{title:"X-ray uppföljning",preview:"Resultat onsdag"}],
};

const no = {
  A: [{title:"Apotek hente resept",preview:"Vitamin D + skjoldbrusk"},{title:"Anna bursdag",preview:"Kort + liten gave"},{title:"Auto til verksted",preview:"Oljeskift"}],
  B: [{title:"Bank møte torsdag",preview:"Legitimasjon + lønnsslipp"},{title:"Bakeri lørdag morgen",preview:"Rundstykker + brød"},{title:"Barn hente skole",preview:"Onsdag 14:15"}],
  C: [{title:"Coop lørdag storhandel",preview:"Liste på kjøleskap"},{title:"Café med Mia onsdag",preview:"Ved kontoret hennes"},{title:"Cykel dekk reparere",preview:"Bak — mister luft"}],
  D: [{title:"Dentist onsdag 9:20",preview:"Undersøkelse + røntgen"},{title:"Dokument bank skanne",preview:"Sende i morgen"},{title:"Diskmaskin reparasjon",preview:"Ringe tekniker"}],
  E: [{title:"Email Sam svare",preview:"Ang. faktura forrige måned"},{title:"Emma dansefremvisning",preview:"Lørdag 19, blå kjole"},{title:"El-avtale fornye",preview:"Sammenlign 3 leverandører"}],
  F: [{title:"Frisør fredag 18",preview:"Som forrige gang"},{title:"Faktura #204 sende",preview:"Månedens slutt, 30 dager"},{title:"Fotball trening Emma",preview:"Tirsdag 17"}],
  G: [{title:"Gym tirsdag",preview:"Ben + core 45 min"},{title:"Grønnsaker torg lørdag",preview:"Tomat + spinat"},{title:"Gitar strenger bytte",preview:"Elixir 10 som vanlig"}],
  H: [{title:"Husleie betale 1.",preview:"AvtaleGiro klart"},{title:"Hår klippe fredag",preview:"Kortere denne gang"},{title:"Hund veterinær",preview:"Årsvaksine"}],
  I: [{title:"IKEA lørdag",preview:"Hylle + potter + kjøttboller"},{title:"Idé gave Alex",preview:"Bok + gavekort"},{title:"Internett leverandør bytte",preview:"Sammenligne pris"}],
  J: [{title:"Jobbmøte torsdag",preview:"Rom 3, kl 10"},{title:"Julekort liste",preview:"Familie + venner"},{title:"Julia bursdag",preview:"Kort + gave"}],
  K: [{title:"Klippe plen søndag",preview:"Om det er tørt"},{title:"Kaffe bønner bestille",preview:"To kilo mørkbrent"},{title:"Kaffe med Mia onsdag",preview:"Ved kontoret hennes"}],
  L: [{title:"Lønn sjekke",preview:"Skal komme 25:e"},{title:"Lege årlig kontroll",preview:"Bestille denne måneden"},{title:"Lena ringe søndag",preview:"Om ferieturen"}],
  M: [{title:"Mamma bursdag",preview:"14 mai — blomster + ring"},{title:"Møte Q3 gjennomgang",preview:"Tall opp 12%"},{title:"Matlaging søndag",preview:"Ukens lunsjer"}],
  N: [{title:"Notarius kontrakt",preview:"Torsdag 15"},{title:"Nye briller bestille",preview:"Ny resept"},{title:"Netflix serie liste",preview:"Islandske krim"}],
  O: [{title:"Optiker fredag 16",preview:"Ny brille resept"},{title:"Olje bilbytte",preview:"Servicelys på"},{title:"Ombooking tannlege",preview:"Ringe resepsjon"}],
  P: [{title:"Pakke hente Posten",preview:"Innen fredag"},{title:"Pass fornye",preview:"Foto + time politi"},{title:"Pappa ringe søndag",preview:"Rundt 15"}],
  Q: [{title:"Quinoa salat søndag",preview:"Feta + granateple"},{title:"Quiz kveld torsdag",preview:"Team 4 — trenger en til"}],
  R: [{title:"Regning strøm autogiro",preview:"Sette opp denne måneden"},{title:"Risotto sopp oppskrift",preview:"Buljong + parmesan"},{title:"Restaurant lørdag booking",preview:"Bord 4, 20:30"}],
  S: [{title:"Systembolag fredag",preview:"To hvit, en rød"},{title:"Sport tirsdag",preview:"Push day — bryst + triceps"},{title:"Sara bursdag",preview:"Kort + gave"}],
  T: [{title:"Tannlege onsdag",preview:"Undersøkelse + tannstein"},{title:"Teater fredag billetter",preview:"Rad H, plass 12 & 13"},{title:"Trening lørdag lang tur",preview:"Med Marcus"}],
  U: [{title:"Uber flyplass bestille",preview:"Kveld før, 5:15"},{title:"Universitet søknad barn",preview:"Innen 15."},{title:"Underholdning barn app",preview:"Laste ned før reisen"}],
  V: [{title:"Veterinær hund",preview:"Årsvaksine"},{title:"Vaske klær søndag",preview:"Laken + håndklær"},{title:"Vin fredag to flasker",preview:"En hvit, en rød"}],
  W: [{title:"Weekend planlegge lørdag",preview:"Handle, løpe, brunch"},{title:"Wifi router omstart",preview:"Ny konfigurasjon"},{title:"Whisky gave Alex",preview:"Skotsk, favoritt"}],
  X: [{title:"X-ray oppfølging",preview:"Resultat onsdag"}],
};

const da = {
  A: [{title:"Apotek hente recept",preview:"Vitamin D + skjoldbrusk"},{title:"Anna fødselsdag",preview:"Kort + lille gave"},{title:"Auto til værksted",preview:"Olieskift"}],
  B: [{title:"Bank møde torsdag",preview:"ID + lønseddel"},{title:"Bageri lørdag morgen",preview:"Rundstykker + brød"},{title:"Børn hente skole",preview:"Onsdag 14:15"}],
  C: [{title:"Coop lørdag storhandel",preview:"Liste på køleskab"},{title:"Café med Mia onsdag",preview:"Ved hendes kontor"},{title:"Cykel dæk reparere",preview:"Bag — mister luft"}],
  D: [{title:"Dentist onsdag 9:20",preview:"Undersøgelse + røntgen"},{title:"Dokument bank scanne",preview:"Sende i morgen"},{title:"Diskmaskine reparation",preview:"Ringe tekniker"}],
  E: [{title:"Email Sam svare",preview:"Ang. faktura sidste måned"},{title:"Emma danseoptræden",preview:"Lørdag 19, blå kjole"},{title:"El-aftale forny",preview:"Sammenlign 3 leverandører"}],
  F: [{title:"Frisør fredag 18",preview:"Som sidste gang"},{title:"Faktura #204 sende",preview:"Slutning af måned"},{title:"Fodbold Emma træning",preview:"Tirsdag 17"}],
  G: [{title:"Gym tirsdag",preview:"Ben + core 45 min"},{title:"Grøntsager torv lørdag",preview:"Tomat + spinat"},{title:"Guitar strenge skifte",preview:"Elixir 10 som sædvanlig"}],
  H: [{title:"Husleje betale 1.",preview:"Automatisk overførsel"},{title:"Hår klippe fredag",preview:"Kortere denne gang"},{title:"Hund dyrlæge",preview:"Årsvaccine"}],
  I: [{title:"IKEA lørdag",preview:"Hylde + potter + kødboller"},{title:"Idé gave Alex",preview:"Bog + gavekort"},{title:"Internet leverandør skifte",preview:"Sammenligne pris"}],
  J: [{title:"Jobmøde torsdag",preview:"Lokale 3, kl 10"},{title:"Julekort liste",preview:"Familie + venner"},{title:"Julia fødselsdag",preview:"Kort + gave"}],
  K: [{title:"Klippe græs søndag",preview:"Hvis tørt"},{title:"Kaffe bønner bestille",preview:"To kilo mørkristet"},{title:"Kaffe med Mia onsdag",preview:"Nær hendes arbejde"}],
  L: [{title:"Løn tjekke konto",preview:"Kommer 25."},{title:"Læge årlig kontrol",preview:"Bestille denne måned"},{title:"Lena ringe søndag",preview:"Om ferien"}],
  M: [{title:"Mor fødselsdag",preview:"14 maj — blomster + ring"},{title:"Møde Q3 gennemgang",preview:"Tal op 12%"},{title:"Madlavning søndag",preview:"Ugens frokoster"}],
  N: [{title:"Notar kontrakt",preview:"Torsdag 15"},{title:"Nye briller bestille",preview:"Ny recept"},{title:"Netflix serie liste",preview:"Islandsk krimi"}],
  O: [{title:"Optiker fredag 16",preview:"Ny brille recept"},{title:"Olie bil skift",preview:"Servicelampe tændt"},{title:"Ombooking tandlæge",preview:"Ringe reception"}],
  P: [{title:"Pakke hente Post",preview:"Inden fredag"},{title:"Pas forny",preview:"Foto + tid politi"},{title:"Far ringe søndag",preview:"Omkring 15"}],
  Q: [{title:"Quinoa salat søndag",preview:"Feta + granatæble"},{title:"Quiz aften torsdag",preview:"Team 4 — mangler én"}],
  R: [{title:"Regning strøm autogiro",preview:"Sætte op denne måned"},{title:"Risotto svampe opskrift",preview:"Bouillon + parmesan"},{title:"Restaurant lørdag booking",preview:"Bord 4, 20:30"}],
  S: [{title:"Supermarked lørdag",preview:"Liste på køleskab"},{title:"Sport tirsdag",preview:"Push day — bryst + triceps"},{title:"Sara fødselsdag",preview:"Kort + gave"}],
  T: [{title:"Tandlæge onsdag",preview:"Undersøgelse + tandsten"},{title:"Teater fredag billetter",preview:"Række H, plads 12 & 13"},{title:"Terapi onsdag 17",preview:"Tage dagbog med"}],
  U: [{title:"Uber lufthavn bestille",preview:"Aftenen før, 5:15"},{title:"Universitet ansøgning barn",preview:"Inden 15."},{title:"Underholdning børn app",preview:"Downloade før rejsen"}],
  V: [{title:"Dyrlæge hund",preview:"Årsvaccine"},{title:"Vaske tøj søndag",preview:"Lagen + håndklæder"},{title:"Vin fredag to flasker",preview:"En hvid, en rød"}],
  W: [{title:"Weekend planlægge lørdag",preview:"Handle, løbe, brunch"},{title:"Wifi router genstart",preview:"Ny konfiguration"},{title:"Whisky gave Alex",preview:"Skotsk favorit"}],
  X: [{title:"X-ray opfølgning",preview:"Resultat onsdag"}],
};

const fi = {
  A: [{title:"Apteekki reseptin nouto",preview:"D-vitamiini + kilpirauhanen"},{title:"Anna syntymäpäivä",preview:"Kortti + pieni lahja"},{title:"Auto huoltoon",preview:"Öljynvaihto"}],
  B: [{title:"Bussikortti lataus",preview:"30 päivän lippu"},{title:"Bank tapaaminen torstai",preview:"Henkilöllisyys + palkkakuitit"},{title:"BBQ lauantai",preview:"Sämpylät, salaatti, jäät"}],
  C: [{title:"Café Mian kanssa keskiviikko",preview:"Hänen toimistonsa lähellä"},{title:"Coop viikonlopun ostokset",preview:"Lista jääkaapin päällä"},{title:"Chef illallinen lauantai",preview:"Neljälle, klo 20"}],
  D: [{title:"Dentisti keskiviikko 9:20",preview:"Tarkastus + kuvat"},{title:"Dokumentit pankki skannaus",preview:"Lähetetään huomenna"},{title:"Diskmaskin korjaus",preview:"Soita huoltoon"}],
  E: [{title:"Email Samille vastaus",preview:"Viime kuun lasku"},{title:"Emma tanssiesitys",preview:"Lauantai 19, sininen mekko"},{title:"Energiasopimus vertailu",preview:"Kolme tarjousta"}],
  F: [{title:"Frisööri perjantai 18",preview:"Sama kuin viimeksi"},{title:"Facturaa #204 lähetä",preview:"Kuun lopussa, 30 päivää"},{title:"Fotball Emman treeni",preview:"Tiistai 17"}],
  G: [{title:"Gym tiistai",preview:"Jalat + core 45 min"},{title:"Groceries lauantai",preview:"Maito, munat, banaanit"},{title:"Giitar strings vaihto",preview:"Elixir 10s"}],
  H: [{title:"Hammaslääkäri keskiviikko",preview:"Tarkastus + kivet"},{title:"Hiukset leikkuu perjantai",preview:"Lyhyemmät tällä kertaa"},{title:"Hotelli Rooma varaus",preview:"Lähellä keskustaa"}],
  I: [{title:"IKEA lauantai",preview:"Hylly + ruukut + pyörykät"},{title:"Idea lahja Alex",preview:"Kirja + lahjakortti"},{title:"Internetliittymä vaihto",preview:"Vertaile hintoja"}],
  J: [{title:"Joulukortit lista",preview:"Perhe + työ"},{title:"Julia syntymäpäivä",preview:"Kortti + lahja"},{title:"Job kokous torstai",preview:"Huone 3, klo 10"}],
  K: [{title:"Kampaaja perjantai 18",preview:"Sama kuin viimeksi"},{title:"Kissa eläinlääkäri",preview:"Vuosirokotukset"},{title:"Kahvi papuja tilaa",preview:"Kaksi kiloa tumma paahto"}],
  L: [{title:"Lääkäri vuosikontrolli",preview:"Varaa tässä kuussa"},{title:"Lena soitto sunnuntai",preview:"Kesälomasta"},{title:"Lelut lasten kellari",preview:"Lahjoita käyttämättömät"}],
  M: [{title:"Mummon syntymäpäivä",preview:"14. toukokuuta — kukat"},{title:"Meeting Q3 review",preview:"Numerot ylös 12%"},{title:"Mekaanikko auto tarkastus",preview:"Perjantai 10"}],
  N: [{title:"Notaari sopimus",preview:"Torstai 15"},{title:"Nya glasögon lasit",preview:"Uusi resepti"},{title:"Netflix sarjoja lista",preview:"Islantilainen dekkari"}],
  O: [{title:"Optikko perjantai 16",preview:"Uudet lasit"},{title:"Öljynvaihto auto",preview:"Huoltovalo palanut"},{title:"Ompelija housut lyhyemmiksi",preview:"Vie perjantaina"}],
  P: [{title:"Paketti nouto Posti",preview:"Ennen perjantaita"},{title:"Passi uusiminen",preview:"Kuva + aika poliisi"},{title:"Pilates lauantai 9",preview:"Varaa ennen torstaita"}],
  Q: [{title:"Quinoa salaatti sunnuntai",preview:"Feta + granaattiomena"},{title:"Quiz-ilta torstai",preview:"Joukkue 4 — tarvitaan yksi"}],
  R: [{title:"Resepti sieni-risotto",preview:"Liemi + parmesaani"},{title:"Ravintolavaraus lauantai",preview:"Pöytä 4, klo 20:30"},{title:"Rooman matka varaus",preview:"Lento + hotelli"}],
  S: [{title:"Supermarket lauantai",preview:"Lista jääkaapin päällä"},{title:"Sara syntymäpäivä",preview:"Kortti + lahja"},{title:"Sunnuntai brunssi varaus",preview:"Pöytä 4, klo 11:30"}],
  T: [{title:"Tandläkare keskiviikko",preview:"Tarkastus + hammaskivi"},{title:"Teatteri perjantai liput",preview:"Rivi H, paikat 12 & 13"},{title:"Terapia keskiviikko 17",preview:"Ota päiväkirja mukaan"}],
  U: [{title:"Uber lentokentälle",preview:"Illalla ennen, 5:15"},{title:"Universitet lasten hakemus",preview:"Ennen 15."},{title:"Uinti tiistai 7:30",preview:"Rata 3 varattu"}],
  V: [{title:"Veterinär koira",preview:"Vuosirokotukset"},{title:"Vaatteet pesu sunnuntai",preview:"Lakanat + pyyhkeet"},{title:"Viini perjantai kaksi pulloa",preview:"Yksi valkoinen, yksi punainen"}],
  W: [{title:"Weekend suunnittelu lauantai",preview:"Kauppa, lenkki, brunssi"},{title:"Wifi reititin uudelleen",preview:"Uusi konfiguraatio"},{title:"Whisky lahja Alex",preview:"Skotti suosikki"}],
  X: [{title:"X-ray tulokset",preview:"Keskiviikko soittaa"}],
};

const cs = {
  A: [{title:"Anna narozeniny",preview:"Přání + malý dárek"},{title:"Auto do servisu",preview:"Výměna oleje"},{title:"Apríl žertík na Marka",preview:"Vymyslet něco"}],
  B: [{title:"Banka schůzka čtvrtek",preview:"Doklad + výplatní pásky"},{title:"Bazén úterý 7:30",preview:"Dráha 3 rezervovaná"},{title:"Bytu prohlídka čtvrtek",preview:"Potvrzení o příjmu"}],
  C: [{title:"Cukrárna sobota ráno",preview:"Rohlíky + chleba"},{title:"Café s Miou středa",preview:"U její kanceláře"},{title:"Cvičení úterý",preview:"Nohy + core 45 min"}],
  D: [{title:"Dentista středa 9:20",preview:"Kontrola + panoramatický snímek"},{title:"Dokumenty banka skenovat",preview:"Poslat zítra"},{title:"Domácí úklid sobota",preview:"Kuchyň + koupelna"}],
  E: [{title:"Email Sam odpovědět",preview:"K faktuře minulý měsíc"},{title:"Emma taneční vystoupení",preview:"Sobota 19, modré šaty"},{title:"Energie smlouva porovnat",preview:"Tři dodavatelé"}],
  F: [{title:"Faktura #204 poslat",preview:"Konec měsíce, 30 dní"},{title:"Fyzioterapie úterý",preview:"Záda + krční páteř"},{title:"Fotbal Emma trénink",preview:"Úterý 17"}],
  G: [{title:"Grilování sobota",preview:"Housky, salát, led"},{title:"Gym úterý",preview:"Nohy + core 45 min"},{title:"Gitarové struny výměna",preview:"Elixir 10s jako obvykle"}],
  H: [{title:"Hodinky baterie výměna",preview:"Klenotník u nádraží"},{title:"Hodinky s Michalem",preview:"Kavárna Vltavská pátek"},{title:"Holič pátek 18",preview:"Jako minule"}],
  I: [{title:"IKEA sobota",preview:"Regál + květináče + kuličky"},{title:"Idea dárek Alex",preview:"Kniha + poukaz"},{title:"Internet operátor změna",preview:"Porovnat ceny"}],
  J: [{title:"Jana narozeniny",preview:"Přání + poukázka"},{title:"Jóga sobota 9",preview:"Rezervovat do čtvrtka"},{title:"Jitrnice u řezníka",preview:"Sobota ráno"}],
  K: [{title:"Kadeřník pátek 18",preview:"Jako minule, kratší"},{title:"Kočka veterinář",preview:"Roční očkování"},{title:"Klíče od auta duplikát",preview:"Zámečník tento týden"}],
  L: [{title:"Lékař roční prohlídka",preview:"Objednat tento měsíc"},{title:"Lena volat neděle",preview:"O dovolené"},{title:"Lidl sobota nákup",preview:"Seznam na ledničce"}],
  M: [{title:"Máma narozeniny",preview:"14. května — květiny + telefon"},{title:"Meeting Q3 přehled",preview:"Čísla nahoru o 12 %"},{title:"Manžel volání odpoledne",preview:"O večerních plánech"}],
  N: [{title:"Notář smlouva čtvrtek",preview:"Plná moc"},{title:"Nájem zaplatit 1.",preview:"Trvalý příkaz nastaven"},{title:"Netflix seriály seznam",preview:"Islandská krimi"}],
  O: [{title:"Oční pátek 16",preview:"Nový recept na brýle"},{title:"Olej výměna auto",preview:"Servisní kontrolka svítí"},{title:"Osvědčení školka podepsat",preview:"Zítra ráno"}],
  P: [{title:"Prádelna vyzvednout",preview:"Oblek + dvě košile"},{title:"Pas obnovit",preview:"Foto + termín úřad"},{title:"Pilates sobota 9",preview:"Rezervovat před čtvrtkem"}],
  Q: [{title:"Quinoa salát neděle",preview:"Feta + granátové jablko"},{title:"Quiz večer čtvrtek",preview:"Tým 4 — chybí jeden"}],
  R: [{title:"Recept houbové rizoto",preview:"Vývar + parmezán"},{title:"Rezervace restaurace sobota",preview:"Stůl pro 4, 20:30"},{title:"Řidičák obnovit",preview:"Foto + lékařská zpráva"}],
  S: [{title:"Supermarket sobota",preview:"Seznam na ledničce"},{title:"Sportovní úterý",preview:"Push day — hrudník + tricepsy"},{title:"Sára narozeniny",preview:"Přání + dárek"}],
  T: [{title:"Tomáš narozeniny",preview:"Přání + poukázka"},{title:"Terapie středa 17",preview:"Vzít deník s sebou"},{title:"Trenér tenisu úterý",preview:"18:00 kurty"}],
  U: [{title:"Uber letiště objednat",preview:"Večer předem, 5:15"},{title:"Univerzita přihláška syn",preview:"Do 15."},{title:"Úklid víkendový plán",preview:"Kuchyň + koupelna"}],
  V: [{title:"Veterinář pes",preview:"Roční očkování"},{title:"Vlasy pátek 18",preview:"Jako minule"},{title:"Vinný sklep zásoby",preview:"Dvě bílá, jedno červené"}],
  W: [{title:"Weekend plán sobota",preview:"Nákup, běh, brunch"},{title:"Wifi router restart",preview:"Nová konfigurace"},{title:"Whisky dárek Alex",preview:"Skotská oblíbená"}],
  X: [{title:"X-ray kontrola výsledky",preview:"Středa zavolat"}],
};

const ro = {
  A: [{title:"Aeroport sâmbătă 18",preview:"Terminal 2 sosiri"},{title:"Amazon retur colet",preview:"Adidași + husă telefon"},{title:"Ana ziua ei",preview:"Felicitare + cadou mic"}],
  B: [{title:"Bancă întâlnire",preview:"Buletin + fluturași"},{title:"Bibliotecă returnare carte",preview:"Înainte de marți"},{title:"Bâlci copii sâmbătă",preview:"Bilete online"}],
  C: [{title:"Coafor vineri 18",preview:"Ca data trecută, mai scurt"},{title:"Cabinet stomatologic",preview:"Miercuri 9:20 igienizare"},{title:"Cadou Alex",preview:"Carte + voucher"}],
  D: [{title:"Dentist miercuri",preview:"Detartraj + panoramică"},{title:"Documente bancă scanare",preview:"Trimis mâine"},{title:"Duminică prânz socri",preview:"De adus desert"}],
  E: [{title:"Email Sam de răspuns",preview:"Despre factura de luna trecută"},{title:"Emma spectacol dans",preview:"Sâmbătă 19, rochie bleumarin"},{title:"Enel contract compara",preview:"Trei oferte"}],
  F: [{title:"Factură #204 trimite",preview:"Sfârșit lună, 30 zile"},{title:"Farmacie ridicare rețetă",preview:"Vitamina D + tiroida"},{title:"Fotbal Emma antrenament",preview:"Marți 17"}],
  G: [{title:"Grădină plantare primăvară",preview:"Busuioc + mentă"},{title:"Gym marți",preview:"Picioare + core 45 min"},{title:"Grătar sâmbătă",preview:"Chifle + salată + gheață"}],
  H: [{title:"Hipotecă întâlnire bancă",preview:"Comparație 3 rate"},{title:"Hotel Roma rezervare",preview:"Aproape de centru"},{title:"Haine curățătorie",preview:"Costum + două cămăși"}],
  I: [{title:"IKEA sâmbătă",preview:"Raft + ghivece + chiftele"},{title:"Impozit chitanțe adună",preview:"Utilități + freelance"},{title:"Interviu pregătire",preview:"Cercetare firmă + trei întrebări"}],
  J: [{title:"Jocuri copii sortare",preview:"Donare cele nefolosite"},{title:"Judecătorie termen",preview:"Miercuri 9, sala 3"},{title:"Jocuri video Marco cadou",preview:"Ziua lui, luna viitoare"}],
  K: [{title:"Karaoke vineri seara",preview:"Aniversare Sam, rezervare"},{title:"Kit prim-ajutor mașină",preview:"Verifica termene"},{title:"Karate copil ianuarie",preview:"Sală lângă bloc"}],
  L: [{title:"Lună mai — călătorie",preview:"Rezerv zbor + hotel"},{title:"Lidl sâmbătă cumpărături",preview:"Listă pe frigider"},{title:"Locul de joacă duminică",preview:"Cu Emma și verișorii"}],
  M: [{title:"Mama zi de naștere",preview:"14 mai — flori + telefon"},{title:"Meeting Q3 review",preview:"Cifre în urcare 12%"},{title:"Mecanic mașină verificare",preview:"Vineri 10"}],
  N: [{title:"Notar semnătură joi",preview:"Procură pentru frate"},{title:"Nouă cameră Emma zugrăvit",preview:"Sâmbătă dimineața"},{title:"Nepot vizită duminică",preview:"Ora 15"}],
  O: [{title:"Oftalmolog vineri 16",preview:"Rețetă nouă"},{title:"Ochelari online comanda",preview:"Rețetă nouă"},{title:"Ora sport copil vineri",preview:"Echipament în ghiozdan"}],
  P: [{title:"Poștă ridicare colet",preview:"Până vineri"},{title:"Pașaport reînnoire",preview:"Foto + programare"},{title:"Părinți sună duminică",preview:"Pe la 15"}],
  Q: [{title:"Quinoa salată duminică",preview:"Feta + rodie"},{title:"Quiz seara joi",preview:"Echipă de 4 — mai trebuie unul"}],
  R: [{title:"Rețetă risotto ciuperci",preview:"Supă + parmezan"},{title:"Restaurant sâmbătă rezervare",preview:"Masă 4 persoane, 20:30"},{title:"Renovare bucătărie oferte",preview:"Trei firme săptămâna viitoare"}],
  S: [{title:"Supermarket sâmbătă",preview:"Listă pe frigider"},{title:"Sport marți",preview:"Push day — piept + triceps"},{title:"Sara ziua ei",preview:"Felicitare + cadou"}],
  T: [{title:"Terapeut miercuri 17",preview:"Adus jurnal"},{title:"Teatru bilete vineri",preview:"Rândul H, locurile 12 și 13"},{title:"Trenul de Cluj rezervare",preview:"Vineri 8:45 CFR"}],
  U: [{title:"Uber aeroport programare",preview:"Cu o seară înainte, 5:15"},{title:"Universitate dosar fiu",preview:"Termen 15"},{title:"Unghii vineri 17",preview:"La fel ca ultima dată"}],
  V: [{title:"Veterinar câine",preview:"Vaccinuri anuale"},{title:"Vacanță august plan",preview:"Două săptămâni litoral"},{title:"Verificare cont energie",preview:"Debit automat"}],
  W: [{title:"Weekend cumpărături sâmbătă",preview:"Piață + supermarket"},{title:"Wifi router repornire",preview:"Configurare nouă"},{title:"WhatsApp Marta răspuns",preview:"Despre vacanța din august"}],
  X: [{title:"X-ray control rezultate",preview:"Miercuri sună"}],
};

const hu = {
  A: [{title:"Anna szülinapja",preview:"Képeslap + kis ajándék"},{title:"Autó szerviz",preview:"Olajcsere"},{title:"Ablakmosás szombat",preview:"Ha nem esik"}],
  B: [{title:"Bank időpont",preview:"Igazolvány + fizetési papírok"},{title:"Bicikli szerelés",preview:"Hátsó kerék tömlő"},{title:"Bevásárlás szombat",preview:"Lista a hűtőn"}],
  C: [{title:"Cukrászda szombat reggel",preview:"Zsemle + kifli"},{title:"Csoki torta vasárnap",preview:"Cukormáz nélkül"},{title:"Café Miával szerda",preview:"Az irodája mellett"}],
  D: [{title:"Dentista szerda 9:20",preview:"Kontroll + fogkő eltávolítás"},{title:"Dokumentumok bankba",preview:"Szkennelés + email"},{title:"Domi születésnap",preview:"Kártya + utalvány"}],
  E: [{title:"Emailre Samnek válaszolni",preview:"Múlt havi számla ügyben"},{title:"Emma tánc előadás",preview:"Szombat 19, kék ruha"},{title:"Elektromos szerződés",preview:"Három ajánlat összehasonlítása"}],
  F: [{title:"Frizura péntek 18",preview:"Ugyanaz mint legutóbb"},{title:"Fizio kedd 15:30",preview:"Hát + nyak"},{title:"Fogorvos szerda kontroll",preview:"Éves szűrés"}],
  G: [{title:"Gyümölcsös piac szombat",preview:"Alma + narancs + banán"},{title:"Gyerek iskola elhozni",preview:"Szerda korán 14:15"},{title:"Gitár húr csere",preview:"Elixir 10-es mint mindig"}],
  H: [{title:"Hajvágás péntek 18",preview:"Rövidebbre most"},{title:"Hitelügyintézés bank",preview:"Csütörtök 15"},{title:"Háziorvos ellenőrzés",preview:"Éves szűrés"}],
  I: [{title:"IKEA szombat",preview:"Polc + cserepek + húsgombóc"},{title:"Idős rokon látogatás",preview:"Vasárnap 15 óra"},{title:"Internet szolgáltató váltás",preview:"Ár összehasonlítás"}],
  J: [{title:"Jóga szombat 9",preview:"Csütörtökig lefoglalni"},{title:"Játékok gyerekek szortírozás",preview:"Használatlanokat adományozni"},{title:"Járda seprése szombat",preview:"Levelek"}],
  K: [{title:"Kávé Miával szerda",preview:"Az irodája mellett"},{title:"Kutya állatorvos",preview:"Éves oltások"},{title:"Kalapos üzlet",preview:"Nyári kalap"}],
  L: [{title:"Levél posta feladni",preview:"Péntekig"},{title:"Lila festék bolt",preview:"Emma szoba"},{title:"Léa születésnap",preview:"Képeslap + ajándék"}],
  M: [{title:"Mama szülinap",preview:"Május 14 — virág + telefon"},{title:"Meeting Q3 összegzés",preview:"12% növekedés"},{title:"Mérés doki éves",preview:"Ebben a hónapban"}],
  N: [{title:"Nagyi látogatás vasárnap",preview:"Süti + virág"},{title:"Notár szerződés csütörtök",preview:"Meghatalmazás"},{title:"Netflix új sorozat",preview:"Izlandi krimi"}],
  O: [{title:"Optikus péntek 16",preview:"Új recept szemüveg"},{title:"Olajcsere autó",preview:"Szerviz jelzés"},{title:"Óvoda értesítő aláírni",preview:"Reggel elvitel"}],
  P: [{title:"Posta csomag átvétel",preview:"Péntekig"},{title:"Pilates szombat 9",preview:"Csütörtökig lefoglalni"},{title:"Papa telefon vasárnap",preview:"15 óra körül"}],
  Q: [{title:"Quinoa saláta vasárnap",preview:"Feta + gránátalma"},{title:"Quiz est csütörtök",preview:"4 fő — kell még egy"}],
  R: [{title:"Recept gomba rizottó",preview:"Alaplé + parmezán"},{title:"Restaurant szombat foglalás",preview:"4 fő, 20:30"},{title:"Ruhatisztító elvinni",preview:"Öltöny + két ing"}],
  S: [{title:"Szupermarket szombat",preview:"Lista a hűtőn"},{title:"Sport kedd",preview:"Push day — mell + tricepsz"},{title:"Sára szülinap",preview:"Képeslap + ajándék"}],
  T: [{title:"Tanár beszélgetés Emma",preview:"Csütörtök 16"},{title:"Színház jegy péntek",preview:"H sor, 12 és 13"},{title:"Terápia szerda 17",preview:"Napló magammal"}],
  U: [{title:"Uber reptérre foglalás",preview:"Előző este, 5:15"},{title:"Uzsonna gyerek suli",preview:"Készíteni holnap"},{title:"Új szemüveg megrendelés",preview:"Új recept"}],
  V: [{title:"Vitamin D vásárlás",preview:"Patika + pajzsmirigy"},{title:"Vacsora foglalás szombat",preview:"Asztal 4-nek, 20:30"},{title:"Varrónő szoknya",preview:"Rövidítés péntek"}],
  W: [{title:"Weekend tervek szombat",preview:"Bevásárlás, futás, brunch"},{title:"Wifi router újraindítás",preview:"Új konfiguráció"},{title:"Whisky ajándék Alex",preview:"Skót kedvenc"}],
  X: [{title:"X-ray eredmény orvos",preview:"Szerda telefon"}],
};

const tr = {
  A: [{title:"Anne doğum günü",preview:"14 Mayıs — çiçek + telefon"},{title:"Ambulans sigortası yenile",preview:"Ay sonuna kadar"},{title:"Anahtar yedeği yaptır",preview:"Cumartesi çilingir"}],
  B: [{title:"Banka randevu",preview:"Kimlik + son 3 maaş bordrosu"},{title:"Berber cuma 18",preview:"Aynı geçen sefer gibi"},{title:"Bebek doktoru randevu",preview:"Perşembe 15"}],
  C: [{title:"Cumartesi market",preview:"Buzdolabında liste"},{title:"Ceket kuru temizleme",preview:"Cuma hazır"},{title:"Cem doğum günü",preview:"Kart + küçük hediye"}],
  D: [{title:"Dişçi çarşamba 9:20",preview:"Kontrol + panoramik"},{title:"Doktor kontrol yıllık",preview:"Bu ay randevu al"},{title:"Deterjan almayı unutma",preview:"Market listesine ekle"}],
  E: [{title:"Email Sam'e cevap",preview:"Geçen ay faturasıyla ilgili"},{title:"Emma dans gösterisi",preview:"Cumartesi 19, lacivert elbise"},{title:"Elektrik faturası otomatik",preview:"Bu ay kur"}],
  F: [{title:"Fatura #204 gönder",preview:"Ay sonu, 30 gün"},{title:"Fırın cumartesi",preview:"Ekmek + poğaça"},{title:"Fizyoterapi salı 15:30",preview:"Sırt + boyun"}],
  G: [{title:"Gitar tel değişimi",preview:"Elixir 10 her zamanki"},{title:"Gübre bahçe alışveriş",preview:"Bahar dikimi için"},{title:"Gözlükçü cuma 16",preview:"Yeni reçete"}],
  H: [{title:"Halı yıkatma pazar",preview:"Salonun halısı"},{title:"Hafta sonu tatil planı",preview:"Sahil, iki gün"},{title:"Hediye Sam'in düğünü",preview:"Kayıt listesi + kart"}],
  I: [{title:"IKEA cumartesi",preview:"Raf + saksı + köfte"},{title:"Ilık su borusu tamir",preview:"Tesisatçı salı"},{title:"Iskan belgesi al",preview:"Belediye çarşamba"}],
  J: [{title:"Jimnastik kursu Emma",preview:"Salı ve perşembe 17"},{title:"Julia doğum günü",preview:"Kart + küçük hediye"},{title:"Jenerik yağ değişimi",preview:"Servis lambası yanıyor"}],
  K: [{title:"Kuaför cuma 18",preview:"Biraz daha kısa"},{title:"Kargo teslim al",preview:"Cuma öğleden önce"},{title:"Kedi veteriner",preview:"Yıllık aşılar"}],
  L: [{title:"Lokanta cumartesi rezervasyon",preview:"4 kişilik masa 20:30"},{title:"Lastik değişimi mevsimlik",preview:"Servis pazartesi"},{title:"Laf sözü Marta'ya döneceğim",preview:"Ağustos tatiliyle ilgili"}],
  M: [{title:"Migros cumartesi",preview:"Buzdolabında liste"},{title:"Mektup postaya vermek",preview:"Cuma öncesi"},{title:"Manikür cuma 17",preview:"Aynı geçen sefer gibi"}],
  N: [{title:"Noter perşembe 15",preview:"Vekaletname imza"},{title:"Nişan hediyesi Alex",preview:"Liste + kart"},{title:"Numarası olmayan kanal",preview:"Kablo TV ara"}],
  O: [{title:"Oftalmoloji cuma 16",preview:"Yeni reçete"},{title:"Optik gözlük sipariş",preview:"İnternetten"},{title:"Otomobil yağı değişim",preview:"Servis ışığı yanıyor"}],
  P: [{title:"Postane kargo",preview:"Cuma öncesi"},{title:"Pilates cumartesi 9",preview:"Perşembeye kadar rezervasyon"},{title:"Pasaport yenileme",preview:"Fotoğraf + randevu"}],
  Q: [{title:"Quinoa salatası pazar",preview:"Feta + nar taneleri"},{title:"Quiz gecesi perşembe",preview:"4 kişilik takım — bir kişi daha lazım"}],
  R: [{title:"Randevu doktor yıllık",preview:"Bu ay içinde"},{title:"Reçete apoteğe götür",preview:"D vitamini + tiroid"},{title:"Restoran cumartesi rezervasyon",preview:"4 kişi, 20:30"}],
  S: [{title:"Süpermarket cumartesi",preview:"Buzdolabında liste"},{title:"Sara doğum günü",preview:"Kart + hediye"},{title:"Spor salı",preview:"Bacak + core 45 dakika"}],
  T: [{title:"Tandır sipariş cumartesi",preview:"12 kişilik yaprak sarma"},{title:"Tiyatro bileti cuma",preview:"H sırası, 12 ve 13"},{title:"Terapi çarşamba 17",preview:"Günlük yanımda"}],
  U: [{title:"Uber havaalanı sipariş",preview:"Bir gece önceden, 5:15"},{title:"Uçak biletleri yazdır",preview:"Sigorta + rezervasyon"},{title:"Ünver dükkanı halı",preview:"Yeni ölçü"}],
  V: [{title:"Veteriner köpek",preview:"Yıllık aşı"},{title:"Vergi belgeleri topla",preview:"Serbest çalışma + fatura"},{title:"Vize başvurusu belgeleri",preview:"Fotoğraf + form"}],
  W: [{title:"Wifi router yeniden başlat",preview:"Yeni konfigürasyon"},{title:"WhatsApp Marta cevabı",preview:"Ağustos tatili konusunda"},{title:"Whisky hediye Alex",preview:"İskoç favori marka"}],
  X: [{title:"X-ray sonuç kontrolü",preview:"Çarşamba doktor ara"}],
};

const id = {
  A: [{title:"Apotek ambil resep",preview:"Vitamin D + tiroid"},{title:"Anak jemput sekolah",preview:"Rabu awal 14:15"},{title:"Ayah telepon Minggu",preview:"Sekitar jam 15"}],
  B: [{title:"Bank janji temu",preview:"KTP + tiga slip gaji"},{title:"Bengkel motor Sabtu",preview:"Ganti oli"},{title:"Beli sabun cuci",preview:"Merek biasa"}],
  C: [{title:"Cuci baju Minggu",preview:"Seprai + handuk"},{title:"Cek gula darah",preview:"Puasa 12 jam"},{title:"Coffee dengan Mia Rabu",preview:"Dekat kantornya"}],
  D: [{title:"Dokter gigi Rabu 9:20",preview:"Pemeriksaan + karang gigi"},{title:"Dokumen bank scan",preview:"Kirim besok"},{title:"Diskon Indomaret Sabtu",preview:"Sabun + minyak"}],
  E: [{title:"Email Sam balas",preview:"Faktur bulan lalu"},{title:"Emma pentas tari",preview:"Sabtu 19, gaun biru"},{title:"Ekstrakurikuler anak",preview:"Sabtu pagi 8"}],
  F: [{title:"Faktur #204 kirim",preview:"Akhir bulan, 30 hari"},{title:"Fitnes Selasa",preview:"Kaki + core 45 menit"},{title:"Farmasi tebus obat",preview:"Vitamin + tiroid"}],
  G: [{title:"Gaji cek transfer",preview:"Tanggal 25"},{title:"Gula pasir 2 kilo",preview:"Warung dekat rumah"},{title:"Gym Selasa",preview:"Push day — dada + trisep"}],
  H: [{title:"Hari libur rencana",preview:"Pantai kalau cerah"},{title:"Hadiah ultah Alex",preview:"Buku + voucher"},{title:"Hotel Bali booking",preview:"Dekat pantai"}],
  I: [{title:"IKEA Sabtu",preview:"Rak + pot + bakso"},{title:"Ibu ultah",preview:"14 Mei — bunga + telepon"},{title:"Ide kado Alex",preview:"Buku + voucher"}],
  J: [{title:"Jemput anak sekolah",preview:"Rabu awal 14:15"},{title:"Janji temu dokter",preview:"Cek tahunan"},{title:"Jual sofa lama",preview:"Foto + deskripsi"}],
  K: [{title:"Kartu kredit tagihan",preview:"Auto debit bulan ini"},{title:"Kucing ke dokter hewan",preview:"Vaksin tahunan"},{title:"Kado ultah Sara",preview:"Kartu + hadiah kecil"}],
  L: [{title:"Laundry ambil Jumat",preview:"Jas + dua kemeja"},{title:"Listrik tagihan auto",preview:"Atur bulan ini"},{title:"Liburan Agustus rencana",preview:"Dua minggu pantai"}],
  M: [{title:"Mama ultah",preview:"14 Mei — bunga + telepon"},{title:"Meeting Q3 review",preview:"Angka naik 12%"},{title:"Mobil ke bengkel",preview:"Ganti oli + filter"}],
  N: [{title:"Notaris tanda tangan Kamis",preview:"Surat kuasa"},{title:"Nenek kunjungi Minggu",preview:"Bawa kue"},{title:"Nasi kotak pesan Sabtu",preview:"12 orang"}],
  O: [{title:"Optik pesan online",preview:"Resep baru"},{title:"Oli mobil ganti",preview:"Lampu servis nyala"},{title:"Om telepon Minggu",preview:"Sekitar jam 15"}],
  P: [{title:"Paket ambil kantor pos",preview:"Sebelum Jumat"},{title:"Paspor perpanjang",preview:"Foto + jadwal imigrasi"},{title:"Papa telepon Minggu",preview:"Sekitar jam 15"}],
  Q: [{title:"Quinoa salad Minggu",preview:"Feta + delima"},{title:"Quiz malam Kamis",preview:"Tim 4 — butuh satu lagi"}],
  R: [{title:"Rambut potong Jumat 18",preview:"Sedikit lebih pendek"},{title:"Resep risoto jamur",preview:"Kaldu + parmesan"},{title:"Rekening bank cek saldo",preview:"Setelah gaji"}],
  S: [{title:"Supermarket Sabtu",preview:"Daftar di kulkas"},{title:"Sekolah tanda tangan surat",preview:"Izin study tour"},{title:"Sara ultah",preview:"Kartu + hadiah kecil"}],
  T: [{title:"Terapi Rabu 17",preview:"Bawa jurnal"},{title:"Toko buku pesan online",preview:"Buku Emma sekolah"},{title:"Tiket teater Jumat",preview:"Baris H, kursi 12 dan 13"}],
  U: [{title:"Uber ke bandara",preview:"Malam sebelum, 5:15"},{title:"Universitas anak daftar",preview:"Sebelum tanggal 15"},{title:"Undangan pernikahan RSVP",preview:"Sebelum Jumat"}],
  V: [{title:"Vet kucing vaksin",preview:"Cek tahunan"},{title:"Vitamin D tebus",preview:"Farmasi + tiroid"},{title:"Voucher spa mama",preview:"Kado ultah"}],
  W: [{title:"Weekend rencana Sabtu",preview:"Belanja, lari, brunch"},{title:"Wifi router restart",preview:"Konfigurasi baru"},{title:"WhatsApp Marta balas",preview:"Soal liburan Agustus"}],
  X: [{title:"X-ray hasil dokter",preview:"Rabu telepon"}],
};

// ─── EXPORTS ────────────────────────────────────────────────────────
export const LETTER_BANKS = {
  en, nl, es, fr, de, it, pt, pl, sv, no, da, fi, cs, ro, hu, tr, id,
};

/**
 * Resolve the letter-bank for a given langCode (BCP-47 like "nl-BE" or
 * "auto"). Falls back through: exact → base language → English.
 *
 * Non-Latin-script languages (ru, uk, el, he, ar, hi, ja, ko, zh…)
 * are intentionally NOT in LETTER_BANKS — they'd need Latin first
 * letters mid-native-script to work, which reads unnaturally. English
 * is a safer disguise for the acrostic run in those cases.
 */
export function pickLetterBank(langCode) {
  if (!langCode || langCode === "auto") return en;
  const direct = LETTER_BANKS[langCode];
  if (direct) return direct;
  const base = String(langCode).toLowerCase().split("-")[0];
  return LETTER_BANKS[base] || en;
}
